import React, { useEffect, useMemo, useState } from 'react';
import { logger } from '../../utils/logger';
import { PackageIcon, PlusIcon, SearchIcon, EditIcon, TrashIcon, ShoppingCartIcon, CalendarIcon, UserIcon, TagIcon, DollarSignIcon, ArchiveIcon, ArchiveRestoreIcon, PowerOffIcon, AlertTriangleIcon, BarChart2Icon, TrendingUpIcon, TrendingDownIcon, BoxIcon, DownloadIcon, FileTextIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { FormSection } from '../ui/FormSection';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { Product, ProductSale, ProductSaleItem } from '../../types';
import { formatCurrency, getToday } from '../../utils/helpers';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import { useConfirm } from '../../contexts/ConfirmContext';
import { supabase } from '../../utils/supabase';
import { fetchAllRows } from '../../utils/fetchAllRows';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
import { exportProductsToExcel, exportProductSalesToExcel, exportProductStatsToExcel } from '../../utils/excelExport';
import { exportProductsToPdf, exportProductSalesToPdf, exportProductStatsToPdf } from '../../utils/pdfExport';
import { checkLowStockAlerts, clearStockAlert } from '../../utils/stockAlertService';
// Helper to normalize names for comparison
function normalizeName(name: string) {
  if (!name) return '';
  return name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/^(dr\.|dra\.|lic\.|ing\.|ter\.)\s*/g, '').replace(/\s+/g, ' ');
}
const CATEGORIES = [{
  value: 'Cuidado Facial',
  label: 'Cuidado Facial'
}, {
  value: 'Cuidado Corporal',
  label: 'Cuidado Corporal'
}, {
  value: 'Suplementos',
  label: 'Suplementos'
}, {
  value: 'Equipos',
  label: 'Equipos'
}, {
  value: 'Accesorios',
  label: 'Accesorios'
}, {
  value: 'Otro',
  label: 'Otro'
}];

// ─── helpers: Supabase row → app type ────────────────────────────────────────
function rowToProduct(row: any): Product {
  return {
    id: row.id,
    name: row.name,
    sku: row.sku,
    description: row.description ?? '',
    price: Number(row.price),
    cost: Number(row.cost),
    stock: row.stock,
    minStock: row.min_stock ?? 5,
    category: row.category,
    status: row.is_archived ? 'archived' as any : row.status,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at)
  };
}
function rowToSale(row: any): ProductSale {
  return {
    id: row.id,
    date: row.date,
    patientId: row.patient_id ?? '',
    items: row.items as ProductSaleItem[],
    total: Number(row.total),
    notes: row.notes ?? '',
    createdByUserId: row.created_by_user_id,
    createdByUserName: row.created_by_user_name,
    createdAt: new Date(row.created_at)
  };
}
export function Products({
  autoOpenNew = false
}: {
  autoOpenNew?: boolean;
} = {}) {
  const {
    patients,
    addAuditLog
  } = useData();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    addNotification
  } = useNotifications();
  const {
    confirm
  } = useConfirm();
  const {
    isAdmin,
    isTherapist,
    getPermissionLevel,
    canExport
  } = useRolePermissions();
  const canEditSales = isAdmin || isTherapist;
  const statsPermLevel = isAdmin ? 'full' : getPermissionLevel('products_stats') ?? 'none';
  const canViewProductStats = statsPermLevel === 'full' || statsPermLevel === 'read';
  const canExportExcel = canExport('products');
  const canExportPdf = canExport('products');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [stockBannerExpanded, setStockBannerExpanded] = useState(false);
  // Sales exports
  const [isSalesExcelModalOpen, setIsSalesExcelModalOpen] = useState(false);
  const [isSalesExcelExporting, setIsSalesExcelExporting] = useState(false);
  const [isSalesPdfModalOpen, setIsSalesPdfModalOpen] = useState(false);
  const [isSalesPdfExporting, setIsSalesPdfExporting] = useState(false);
  // Stats exports
  const [isStatsExcelModalOpen, setIsStatsExcelModalOpen] = useState(false);
  const [isStatsExcelExporting, setIsStatsExcelExporting] = useState(false);
  const [isStatsPdfModalOpen, setIsStatsPdfModalOpen] = useState(false);
  const [isStatsPdfExporting, setIsStatsPdfExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('catalog');
  const [loading, setLoading] = useState(true);
  // Data State
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<ProductSale[]>([]);
  const [sellers, setSellers] = useState<{
    id: string;
    name: string;
  }[]>([]);
  // ─── Load Data from Supabase ─────────────────────────────────────────────
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        // fetchAllRows pagina la consulta: sin él, Supabase devolvía solo
        // 1000 ventas y los totales históricos salían incompletos.
        const [productsData, salesData, usersData, doctorsData] = await Promise.all([fetchAllRows('products', {
          orderBy: {
            column: 'created_at',
            ascending: false
          }
        }), fetchAllRows('product_sales', {
          orderBy: {
            column: 'date',
            ascending: false
          }
        }), fetchAllRows('users', {
          select: 'id, name, role'
        }), fetchAllRows('doctors', {
          select: 'id, name'
        })]);
        const loadedProducts = (productsData ?? []).map(rowToProduct);
        setProducts(loadedProducts);
        setSales((salesData ?? []).map(rowToSale));
        // Stock alerts on initial load are handled by App.tsx (runs once per login)
        // Do NOT call checkLowStockAlerts here - it would fire every time the module mounts
        // Build sellers list: users with allowed roles + doctors, deduplicated by name
        const allowedRoles = new Set(['doctor', 'doctora', 'terapeuta', 'terapista', 'recepcion', 'recepción', 'recepcionista', 'callcenter', 'call center', 'call_center']);
        const sellersList: {
          id: string;
          name: string;
        }[] = [];
        const seenNormalizedNames = new Set<string>();
        (usersData ?? []).forEach((u: any) => {
          if (!allowedRoles.has((u.role || '').toLowerCase())) return;
          const norm = normalizeName(u.name);
          if (norm && !seenNormalizedNames.has(norm)) {
            seenNormalizedNames.add(norm);
            sellersList.push({
              id: u.id,
              name: u.name
            });
          }
        });
        (doctorsData ?? []).forEach((d: any) => {
          const norm = normalizeName(d.name);
          if (norm && !seenNormalizedNames.has(norm)) {
            seenNormalizedNames.add(norm);
            sellersList.push({
              id: d.id,
              name: d.name
            });
          }
        });
        setSellers(sellersList);
      } catch (e) {
        logger.error('Error loading data from Supabase:', e);
        addNotification({
          type: 'error',
          title: 'Error de carga',
          message: 'No se pudieron cargar los productos desde la base de datos.'
        });
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // ─── Realtime: mantener productos y ventas sincronizados entre usuarios ──
  useEffect(() => {
    const channel = supabase.channel('products-realtime').on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'products'
    }, payload => {
      setProducts(prev => {
        if (prev.some(p => p.id === (payload.new as any).id)) return prev;
        return [rowToProduct(payload.new), ...prev];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'products'
    }, payload => {
      setProducts(prev => prev.map(p => p.id === (payload.new as any).id ? rowToProduct(payload.new) : p));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'products'
    }, payload => {
      setProducts(prev => prev.filter(p => p.id !== (payload.old as any).id));
    }).on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'product_sales'
    }, payload => {
      setSales(prev => {
        if (prev.some(s => s.id === (payload.new as any).id)) return prev;
        return [rowToSale(payload.new), ...prev];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'product_sales'
    }, payload => {
      setSales(prev => prev.map(s => s.id === (payload.new as any).id ? rowToSale(payload.new) : s));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'product_sales'
    }, payload => {
      setSales(prev => prev.filter(s => s.id !== (payload.old as any).id));
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // ─── CATALOG TAB STATE ───────────────────────────────────────────────────
  const [productSearch, setProductSearch] = useState('');
  const [lowStockFilter, setLowStockFilter] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    sku: '',
    description: '',
    price: '',
    cost: '',
    stock: '',
    minStock: '5',
    category: 'Cuidado Facial',
    status: 'active' as 'active' | 'inactive'
  });
  const [productErrors, setProductErrors] = useState<Record<string, string>>({});
  const [archiveConfirmProduct, setArchiveConfirmProduct] = useState<Product | null>(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const filteredProducts = products.filter(p => {
    if (p.status as any === 'archived') return false;
    if (lowStockFilter && p.stock > (p.minStock ?? 5)) return false;
    if (productSearch) {
      const q = productSearch.toLowerCase();
      return p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
    }
    return true;
  });
  const archivedProducts = products.filter(p => p.status as any === 'archived');
  /* ── Stats computations ─────────────────────────────────────────────── */
  const [statsDateFrom, setStatsDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 2);
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [statsDateTo, setStatsDateTo] = useState<string>(getToday());
  const [selectedStatProducts, setSelectedStatProducts] = useState<string[]>([]); // empty = all
  const [productSelectorSearch, setProductSelectorSearch] = useState('');
  const productStats = useMemo(() => {
    const filtered = sales.filter(s => (!statsDateFrom || s.date >= statsDateFrom) && (!statsDateTo || s.date <= statsDateTo));
    const map: Record<string, {
      id: string;
      name: string;
      category: string;
      revenue: number;
      units: number;
      salesCount: number;
      cost: number;
    }> = {};
    filtered.forEach(sale => {
      sale.items.forEach((item: any) => {
        // If products are selected, only include those
        if (selectedStatProducts.length > 0 && !selectedStatProducts.includes(item.productId)) return;
        const prod = products.find(p => p.id === item.productId);
        const id = item.productId;
        const name = prod?.name ?? item.productName ?? 'Desconocido';
        const cat = prod?.category ?? '—';
        const cost = (prod?.cost ?? 0) * Number(item.quantity ?? 0);
        if (!map[id]) map[id] = {
          id,
          name,
          category: cat,
          revenue: 0,
          units: 0,
          salesCount: 0,
          cost: 0
        };
        map[id].revenue += Number(item.subtotal ?? 0);
        map[id].units += Number(item.quantity ?? 0);
        map[id].salesCount += 1;
        map[id].cost += cost;
      });
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [sales, products, statsDateFrom, statsDateTo, selectedStatProducts]);
  const totalStatsRevenue = productStats.reduce((s, p) => s + p.revenue, 0);
  const totalStatsUnits = productStats.reduce((s, p) => s + p.units, 0);
  const totalStatsProfit = productStats.reduce((s, p) => s + (p.revenue - p.cost), 0);
  // Top 5 products by revenue for the chart
  const top5ProductStats = useMemo(() => productStats.slice(0, 5), [productStats]);
  // Revenue trend by month (last months in range)
  const revenueTrend = useMemo(() => {
    const months: Record<string, number> = {};
    sales.filter(s => (!statsDateFrom || s.date >= statsDateFrom) && (!statsDateTo || s.date <= statsDateTo)).forEach(sale => {
      const key = sale.date.slice(0, 7); // YYYY-MM
      months[key] = (months[key] || 0) + sale.total;
    });
    return Object.entries(months).sort(([a], [b]) => a.localeCompare(b)).map(([month, amount]) => ({
      month: new Date(month + '-01').toLocaleDateString('es', {
        month: 'short',
        year: '2-digit'
      }),
      amount
    }));
  }, [sales, statsDateFrom, statsDateTo]);
  const {
    sortedItems: sortedProducts,
    requestSort: requestProductSort,
    isSorted: isProductSorted
  } = useSortableData(filteredProducts);
  const {
    paginatedItems: paginatedProducts,
    currentPage: productPage,
    totalPages: productTotalPages,
    totalItems: productTotalItems,
    startIndex: productStartIndex,
    endIndex: productEndIndex,
    goToPage: productGoToPage,
    nextPage: productNextPage,
    prevPage: productPrevPage
  } = usePagination(sortedProducts);
  // Pagination for the stats detail table
  const {
    paginatedItems: paginatedProductStats,
    currentPage: statsTablePage,
    totalPages: statsTableTotalPages,
    totalItems: statsTableTotalItems,
    startIndex: statsTableStartIndex,
    endIndex: statsTableEndIndex,
    goToPage: statsTableGoToPage
  } = usePagination(productStats, {
    itemsPerPage: 10
  });
  const handleOpenProductModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        sku: product.sku,
        description: product.description,
        price: product.price.toString(),
        cost: product.cost.toString(),
        stock: product.stock.toString(),
        minStock: (product.minStock ?? 5).toString(),
        category: product.category,
        status: product.status
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        sku: '',
        description: '',
        price: '',
        cost: '',
        stock: '',
        minStock: '5',
        category: 'Cuidado Facial',
        status: 'active'
      });
    }
    setProductErrors({});
    setIsProductModalOpen(true);
  };
  // Auto-open new form when navigated with :new param
  useEffect(() => {
    if (autoOpenNew) {
      const t = setTimeout(() => handleOpenProductModal(), 100);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNew]);
  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!productForm.name.trim()) errors.name = 'El nombre es requerido';
    if (!productForm.sku.trim()) errors.sku = 'El SKU es requerido';
    if (!productForm.price || isNaN(Number(productForm.price))) errors.price = 'Precio inválido';
    if (!productForm.cost || isNaN(Number(productForm.cost))) errors.cost = 'Costo inválido';
    if (!productForm.stock || isNaN(Number(productForm.stock))) errors.stock = 'Stock inválido';
    if (Object.keys(errors).length > 0) {
      setProductErrors(errors);
      return;
    }
    const payload = {
      name: productForm.name,
      sku: productForm.sku,
      description: productForm.description,
      price: Number(productForm.price),
      cost: Number(productForm.cost),
      stock: Number(productForm.stock),
      min_stock: Number(productForm.minStock) || 5,
      category: productForm.category,
      status: productForm.status
    };
    confirm({
      title: editingProduct ? 'Guardar Cambios' : 'Crear Producto',
      message: editingProduct ? '¿Estás seguro de que deseas guardar los cambios de este producto?' : '¿Estás seguro de que deseas registrar este nuevo producto?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        try {
          if (editingProduct) {
            const {
              data,
              error
            } = await supabase.from('products').update(payload).eq('id', editingProduct.id).select().single();
            if (error) throw error;
            const updatedProduct = rowToProduct(data);
            const updatedList = products.map(p => p.id === editingProduct.id ? updatedProduct : p);
            setProducts(updatedList);
            // If stock was replenished above minStock, clear the alert record
            if (updatedProduct.stock > updatedProduct.minStock) {
              clearStockAlert(updatedProduct.id);
            } else {
              checkLowStockAlerts([updatedProduct], addNotification);
            }
            addNotification({
              type: 'success',
              title: 'Producto Actualizado',
              message: `${productForm.name} ha sido actualizado.`
            });
            addAuditLog({
              userId: user?.id || 'system',
              userName: user?.name || 'Sistema',
              action: 'update',
              module: 'products',
              entityId: editingProduct.id,
              entityName: productForm.name,
              details: `Producto actualizado: ${productForm.name} (SKU: ${productForm.sku}) - Precio: ${formatCurrency(Number(productForm.price))}, Stock: ${productForm.stock}`
            });
          } else {
            const {
              data,
              error
            } = await supabase.from('products').insert(payload).select().single();
            if (error) throw error;
            const newProduct = rowToProduct(data);
            setProducts([newProduct, ...products]);
            checkLowStockAlerts([newProduct], addNotification);
            addNotification({
              type: 'success',
              title: 'Producto Creado',
              message: `${productForm.name} ha sido agregado al catálogo.`
            });
            addAuditLog({
              userId: user?.id || 'system',
              userName: user?.name || 'Sistema',
              action: 'create',
              module: 'products',
              entityId: data.id,
              entityName: productForm.name,
              details: `Producto creado: ${productForm.name} (SKU: ${productForm.sku}) - Precio: ${formatCurrency(Number(productForm.price))}, Stock: ${productForm.stock}`
            });
          }
          setIsProductModalOpen(false);
        } catch (e: any) {
          logger.error('Error saving product:', e);
          addNotification({
            type: 'error',
            title: 'Error',
            message: e.message ?? 'No se pudo guardar el producto.'
          });
        }
      }
    });
  };
  const handleArchiveProduct = (id: string) => {
    const product = products.find(p => p.id === id);
    if (product) setArchiveConfirmProduct(product);
  };
  const handleDeactivateProduct = async (id: string) => {
    const product = products.find(p => p.id === id);
    if (!product) return;
    confirm({
      title: 'Desactivar Producto',
      message: `¿Estás seguro de que deseas desactivar "${product.name}"?`,
      confirmText: 'Desactivar',
      variant: 'warning',
      onConfirm: async () => {
        try {
          const {
            error
          } = await supabase.from('products').update({
            status: 'inactive'
          }).eq('id', id);
          if (error) throw error;
          setProducts(products.map(p => p.id === id ? {
            ...p,
            status: 'inactive' as any
          } : p));
          addNotification({
            type: 'warning',
            title: 'Producto Desactivado',
            message: `"${product.name}" fue desactivado.`
          });
        } catch (e: any) {
          logger.error('Error deactivating product:', e);
          addNotification({
            type: 'error',
            title: 'Error',
            message: e.message ?? 'No se pudo desactivar el producto.'
          });
        }
      }
    });
  };
  const confirmArchiveProduct = async () => {
    if (!archiveConfirmProduct) return;
    setArchiveLoading(true);
    try {
      const {
        error
      } = await supabase.from('products').update({
        is_archived: true
      }).eq('id', archiveConfirmProduct.id);
      if (error) throw error;
      setProducts(products.map(p => p.id === archiveConfirmProduct.id ? {
        ...p,
        status: 'archived' as any
      } : p));
      addNotification({
        type: 'success',
        title: 'Producto Archivado',
        message: `"${archiveConfirmProduct.name}" fue archivado. Puedes restaurarlo desde Productos Archivados.`
      });
      addAuditLog({
        userId: user?.id || 'system',
        userName: user?.name || 'Sistema',
        action: 'update',
        module: 'products',
        entityId: archiveConfirmProduct.id,
        entityName: archiveConfirmProduct.name,
        details: `Producto archivado: ${archiveConfirmProduct.name} (SKU: ${archiveConfirmProduct.sku || 'N/A'})`
      });
      setArchiveConfirmProduct(null);
    } catch (e: any) {
      logger.error('Error archiving product:', e);
      addNotification({
        type: 'error',
        title: 'Error al archivar',
        message: e.message ?? 'No se pudo archivar el producto.'
      });
    } finally {
      setArchiveLoading(false);
    }
  };
  const handleUnarchiveProduct = async (id: string) => {
    try {
      const product = products.find(p => p.id === id);
      const {
        error
      } = await supabase.from('products').update({
        is_archived: false
      }).eq('id', id);
      if (error) throw error;
      setProducts(products.map(p => p.id === id ? {
        ...p,
        status: 'active' as any
      } : p));
      addNotification({
        type: 'success',
        title: 'Producto Restaurado',
        message: `"${product?.name}" ha sido restaurado al catálogo.`
      });
      addAuditLog({
        userId: user?.id || 'system',
        userName: user?.name || 'Sistema',
        action: 'update',
        module: 'products',
        entityId: id,
        entityName: product?.name || 'Producto',
        details: `Producto restaurado del archivo: ${product?.name || 'Producto'} (SKU: ${product?.sku || 'N/A'})`
      });
    } catch (e: any) {
      logger.error('Error unarchiving product:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo restaurar el producto.'
      });
    }
  };
  // ─── SALES TAB STATE ─────────────────────────────────────────────────────
  const [saleSearch, setSaleSearch] = useState('');
  const [saleSeller, setSaleSeller] = useState('');
  const [saleDateFrom, setSaleDateFrom] = useState('');
  const [saleDateTo, setSaleDateTo] = useState('');
  const [isSaleModalOpen, setIsSaleModalOpen] = useState(false);
  const [editingSale, setEditingSale] = useState<ProductSale | null>(null);
  const [saleForm, setSaleForm] = useState({
    patientId: '',
    sellerId: '',
    date: getToday(),
    notes: ''
  });
  const [saleItems, setSaleItems] = useState<{
    productId: string;
    quantity: number;
    discount: number;
  }[]>([]);
  const [saleErrors, setSaleErrors] = useState<Record<string, string>>({});
  const filteredSales = sales.filter(s => {
    const patient = patients.find(p => p.id === s.patientId);
    const matchesSearch = !saleSearch || patient?.name.toLowerCase().includes(saleSearch.toLowerCase()) || s.createdByUserName.toLowerCase().includes(saleSearch.toLowerCase());
    const matchesSeller = !saleSeller || s.createdByUserId === saleSeller;
    const matchesDateFrom = !saleDateFrom || s.date >= saleDateFrom;
    const matchesDateTo = !saleDateTo || s.date <= saleDateTo;
    return matchesSearch && matchesSeller && matchesDateFrom && matchesDateTo;
  });
  const {
    sortedItems: sortedSales,
    requestSort: requestSaleSort,
    isSorted: isSaleSorted
  } = useSortableData(filteredSales);
  const {
    paginatedItems: paginatedSales,
    currentPage: salePage,
    totalPages: saleTotalPages,
    totalItems: saleTotalItems,
    startIndex: saleStartIndex,
    endIndex: saleEndIndex,
    goToPage: saleGoToPage,
    nextPage: saleNextPage,
    prevPage: salePrevPage
  } = usePagination(sortedSales);
  const handleOpenSaleModal = () => {
    setEditingSale(null);
    setSaleForm({
      patientId: '',
      sellerId: '',
      date: getToday(),
      notes: ''
    });
    setSaleItems([{
      productId: '',
      quantity: 1,
      discount: 0
    }]);
    setSaleErrors({});
    setIsSaleModalOpen(true);
  };
  const handleOpenEditSaleModal = (sale: ProductSale) => {
    setEditingSale(sale);
    setSaleForm({
      patientId: sale.patientId || '',
      sellerId: sale.createdByUserId || '',
      date: sale.date,
      notes: sale.notes || ''
    });
    setSaleItems(sale.items.map((item: any) => ({
      productId: item.productId,
      quantity: item.quantity,
      discount: item.discount || 0
    })));
    setSaleErrors({});
    setIsSaleModalOpen(true);
  };
  const addSaleItem = () => setSaleItems([...saleItems, {
    productId: '',
    quantity: 1,
    discount: 0
  }]);
  const removeSaleItem = (index: number) => setSaleItems(saleItems.filter((_, i) => i !== index));
  const updateSaleItem = (index: number, field: 'productId' | 'quantity' | 'discount', value: any) => {
    const newItems = [...saleItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    setSaleItems(newItems);
    // Clear any row-specific or global "items" error so stale messages don't linger
    setSaleErrors(prev => {
      if (!prev[`item_${index}`] && !prev.items) return prev;
      const next = {
        ...prev
      };
      delete next[`item_${index}`];
      delete next.items;
      return next;
    });
  };
  const calculateSaleTotal = () => saleItems.reduce((total, item) => {
    if (!item.productId) return total;
    const product = products.find(p => p.id === item.productId);
    if (!product) return total;
    const lineTotal = product.price * (item.quantity || 0);
    const discountAmount = lineTotal * ((item.discount || 0) / 100);
    return total + lineTotal - discountAmount;
  }, 0);
  const handleSaveSale = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!saleForm.patientId) errors.patientId = 'Debe seleccionar un cliente/paciente';
    if (!saleForm.sellerId) errors.sellerId = 'Debe seleccionar un vendedor';
    if (!saleForm.date) errors.date = 'La fecha es requerida';
    const validItems = saleItems.filter(item => item.productId && item.quantity > 0);
    if (validItems.length === 0) errors.items = 'Debe agregar al menos un producto válido';
    // For new sales only: check stock. For edits, we check net difference.
    if (!editingSale) {
      validItems.forEach((item, idx) => {
        const product = products.find(p => p.id === item.productId);
        if (product && product.stock < item.quantity) errors[`item_${idx}`] = `Stock insuficiente (${product.stock} disponibles)`;
      });
    } else {
      // For edits: check stock considering items returned from old sale
      validItems.forEach((item, idx) => {
        const product = products.find(p => p.id === item.productId);
        if (!product) return;
        const oldItem = editingSale.items.find((oi: any) => oi.productId === item.productId);
        const returnedQty = oldItem ? (oldItem as any).quantity : 0;
        const availableStock = product.stock + returnedQty;
        if (availableStock < item.quantity) errors[`item_${idx}`] = `Stock insuficiente (${availableStock} disponibles tras devolver la venta anterior)`;
      });
    }
    if (Object.keys(errors).length > 0) {
      setSaleErrors(errors);
      return;
    }
    const finalItems: ProductSaleItem[] = validItems.map(item => {
      const product = products.find(p => p.id === item.productId)!;
      const lineTotal = product.price * item.quantity;
      const discountAmount = lineTotal * ((item.discount || 0) / 100);
      return {
        productId: item.productId,
        productName: product.name,
        quantity: item.quantity,
        unitPrice: product.price,
        discount: item.discount || 0,
        subtotal: lineTotal - discountAmount
      };
    });
    const total = finalItems.reduce((sum, item) => sum + item.subtotal, 0);
    const selectedSeller = sellers.find(s => s.id === saleForm.sellerId);
    const sellerUserId = selectedSeller?.id ?? user?.id ?? 'unknown';
    const sellerUserName = selectedSeller?.name ?? user?.name ?? 'Usuario';
    const executeSaveSale = async () => {
      try {
        if (editingSale) {
          // ── EDIT FLOW ──────────────────────────────────────────────────────
          const {
            error: updateError
          } = await supabase.from('product_sales').update({
            date: saleForm.date,
            patient_id: saleForm.patientId || null,
            items: finalItems,
            total,
            notes: saleForm.notes,
            created_by_user_id: sellerUserId,
            created_by_user_name: sellerUserName
          }).eq('id', editingSale.id);
          if (updateError) throw updateError;
          // Restore stock for old items, then deduct for new items
          const stockUpdates: Record<string, number> = {};
          (editingSale.items as any[]).forEach(oldItem => {
            stockUpdates[oldItem.productId] = (stockUpdates[oldItem.productId] || 0) + oldItem.quantity;
          });
          // -deduct new quantities
          finalItems.forEach(newItem => {
            stockUpdates[newItem.productId] = (stockUpdates[newItem.productId] || 0) - newItem.quantity;
          });
          await Promise.all(Object.entries(stockUpdates).map(([productId, delta]) => {
            const product = products.find(p => p.id === productId);
            if (!product) return Promise.resolve();
            return supabase.from('products').update({
              stock: product.stock + delta
            }).eq('id', productId);
          }));
          // Update local state
          setSales(sales.map(s => s.id === editingSale.id ? {
            ...s,
            date: saleForm.date,
            patientId: saleForm.patientId,
            items: finalItems,
            total,
            notes: saleForm.notes,
            createdByUserId: sellerUserId,
            createdByUserName: sellerUserName
          } : s));
          setProducts(products.map(p => {
            const delta = stockUpdates[p.id];
            return delta !== undefined ? {
              ...p,
              stock: p.stock + delta
            } : p;
          }));
          addNotification({
            type: 'success',
            title: 'Venta Actualizada',
            message: `Venta por ${formatCurrency(total)} actualizada exitosamente.`
          });
          const patientName = patients.find(p => p.id === saleForm.patientId)?.name || 'Cliente';
          const itemsSummary = finalItems.map(item => {
            const base = `${item.quantity}x ${item.productName}`;
            return item.discount ? `${base} (-${item.discount}% desc.)` : base;
          }).join(', ');
          addAuditLog({
            userId: user?.id || 'system',
            userName: user?.name || 'Sistema',
            action: 'update',
            module: 'product_sales',
            entityId: editingSale.id,
            entityName: `Venta a ${patientName}`,
            details: `Venta EDITADA por ${user?.name || 'Usuario'}: ${itemsSummary} - Total: ${formatCurrency(total)} - Cliente: ${patientName} - Vendedor: ${sellerUserName}`
          });
        } else {
          // ── CREATE FLOW ────────────────────────────────────────────────────
          const {
            data: saleData,
            error: saleError
          } = await supabase.from('product_sales').insert({
            date: saleForm.date,
            patient_id: saleForm.patientId || null,
            items: finalItems,
            total,
            notes: saleForm.notes,
            created_by_user_id: sellerUserId,
            created_by_user_name: sellerUserName
          }).select().single();
          if (saleError) throw saleError;
          // Update stock for each product sold
          await Promise.all(finalItems.map(item => {
            const product = products.find(p => p.id === item.productId)!;
            return supabase.from('products').update({
              stock: product.stock - item.quantity
            }).eq('id', item.productId);
          }));
          setSales([rowToSale(saleData), ...sales]);
          const updatedProducts = products.map(p => {
            const soldItem = finalItems.find(item => item.productId === p.id);
            return soldItem ? {
              ...p,
              stock: p.stock - soldItem.quantity
            } : p;
          });
          setProducts(updatedProducts);
          // Check low stock for affected products
          const affectedProducts = updatedProducts.filter(p => finalItems.some(item => item.productId === p.id));
          if (affectedProducts.length > 0) {
            checkLowStockAlerts(affectedProducts, addNotification);
          }
          addNotification({
            type: 'success',
            title: 'Venta Registrada',
            message: `Venta por ${formatCurrency(total)} registrada exitosamente.`
          });
          const patientName = patients.find(p => p.id === saleForm.patientId)?.name || 'Cliente';
          const itemsSummary = finalItems.map(item => {
            const base = `${item.quantity}x ${item.productName}`;
            return item.discount ? `${base} (-${item.discount}% desc.)` : base;
          }).join(', ');
          const hasAnyDiscount = finalItems.some(item => (item.discount || 0) > 0);
          const discountNote = hasAnyDiscount ? ` | Descuento aplicado: ${finalItems.filter(item => (item.discount || 0) > 0).map(item => `${item.productName}: ${item.discount}% (${formatCurrency(item.unitPrice * item.quantity - item.subtotal)})`).join(', ')}` : '';
          addAuditLog({
            userId: user?.id || 'system',
            userName: user?.name || 'Sistema',
            action: 'create',
            module: 'product_sales',
            entityId: saleData.id,
            entityName: `Venta a ${patientName}`,
            details: `Venta registrada por ${sellerUserName}: ${itemsSummary} - Total: ${formatCurrency(total)} - Cliente: ${patientName}${discountNote}`
          });
        }
        setIsSaleModalOpen(false);
        setEditingSale(null);
      } catch (e: any) {
        logger.error('Error saving sale:', e);
        addNotification({
          type: 'error',
          title: 'Error',
          message: e.message ?? 'No se pudo guardar la venta.'
        });
      }
    };
    if (editingSale) {
      // Editar una venta reajusta el stock de los productos (repone las
      // cantidades viejas y descuenta las nuevas) de forma inmediata, así
      // que pedimos confirmación antes de aplicar el cambio.
      const stockChangeSummary = finalItems.map(item => {
        const oldItem = editingSale.items.find((oi: any) => oi.productId === item.productId);
        const oldQty = oldItem ? (oldItem as any).quantity : 0;
        const delta = item.quantity - oldQty;
        if (delta === 0) return null;
        const sign = delta > 0 ? '-' : '+';
        return `${item.productName}: ${sign}${Math.abs(delta)} unid.`;
      }).filter(Boolean).join(', ');
      confirm({
        title: 'Editar Venta',
        message: stockChangeSummary ? `Vas a modificar esta venta. El stock se ajustará: ${stockChangeSummary}. ¿Deseas continuar?` : '¿Estás seguro de que deseas guardar los cambios de esta venta?',
        confirmText: 'Guardar Cambios',
        variant: 'warning',
        onConfirm: () => {
          executeSaveSale();
        }
      });
    } else {
      const itemsList = finalItems.map(item => `${item.quantity}x ${item.productName}`).join(', ');
      confirm({
        title: 'Registrar Venta',
        message: `Vas a registrar esta venta: ${itemsList}. Total: ${formatCurrency(total)}. El stock se descontará automáticamente. ¿Deseas continuar?`,
        confirmText: 'Registrar Venta',
        variant: 'primary',
        onConfirm: () => {
          executeSaveSale();
        }
      });
    }
  };
  const tabs = [{
    id: 'catalog',
    label: 'Catálogo de Productos',
    mobileLabel: 'Catálogo',
    icon: <PackageIcon className="w-4 h-4" data-id="element-4884" data-mp="3cbx759xkb8h9" />
  }, {
    id: 'sales',
    label: 'Ventas de Productos',
    mobileLabel: 'Ventas',
    icon: <ShoppingCartIcon className="w-4 h-4" data-id="element-4885" data-mp="37eolv0h4aobs" />
  }, ...(canViewProductStats ? [{
    id: 'stats',
    label: statsPermLevel === 'read' ? 'Estadísticas (solo lectura)' : 'Estadísticas',
    mobileLabel: 'Estadísticas',
    icon: <BarChart2Icon className="w-4 h-4" data-id="element-4886" data-mp="1wjsuu7c8dk4u" />
  }] : []), {
    id: 'archived',
    label: 'Productos Archivados',
    mobileLabel: 'Archivados',
    icon: <ArchiveIcon className="w-4 h-4" data-id="element-4887" data-mp="1rmkdg8ywpk4n" />
  }];
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    exportProductsToExcel(products, dateFrom, dateTo);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    exportProductsToPdf(products);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const getPatientName = (id: string) => patients.find(p => p.id === id)?.name || 'Cliente';
  const handleSalesExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsSalesExcelExporting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    exportProductSalesToExcel(filteredSales, getPatientName, dateFrom, dateTo);
    setIsSalesExcelExporting(false);
    setIsSalesExcelModalOpen(false);
  };
  const handleSalesExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsSalesPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    await exportProductSalesToPdf(filteredSales, getPatientName, dateFrom, dateTo);
    setIsSalesPdfExporting(false);
    setIsSalesPdfModalOpen(false);
  };
  const handleStatsExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsStatsExcelExporting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    exportProductStatsToExcel(productStats, revenueTrend, {
      revenue: totalStatsRevenue,
      units: totalStatsUnits,
      profit: totalStatsProfit
    }, statsDateFrom, statsDateTo);
    setIsStatsExcelExporting(false);
    setIsStatsExcelModalOpen(false);
  };
  const handleStatsExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsStatsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    await exportProductStatsToPdf(productStats, revenueTrend, {
      revenue: totalStatsRevenue,
      units: totalStatsUnits,
      profit: totalStatsProfit
    }, statsDateFrom, statsDateTo);
    setIsStatsPdfExporting(false);
    setIsStatsPdfModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-4888" data-mp="32dri8icl4snj">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-600 via-orange-500 to-rose-500 shadow-xl text-white" data-id="element-4889" data-mp="32c6pmy9tzjsb">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px)'
      }} data-id="element-4890" data-mp="1lyc5qo3g22wv" />
        
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-4891" data-mp="20lox4tfxn2v6" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-4892" data-mp="29n4yycsolwns" />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full border-[40px] border-white/10 pointer-events-none" data-id="element-4893" data-mp="2elpxm7sac8a4" />
        <div className="absolute -bottom-10 right-32 w-40 h-40 rounded-full opacity-10 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)'
      }} data-id="element-4894" data-mp="f9neiynkpfwz" />
        
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-4895" data-mp="3g43cdew0nlaz" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-4896" data-mp="3g6fqfgynlbir">
          <div data-id="element-4897" data-mp="3q1pvkdkclz0r">
            <div className="flex items-center gap-3 mb-2" data-id="element-4898" data-mp="3q1zj7uhx7u3a">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-4899" data-mp="32cohoasqxvo3">
                <PackageIcon className="w-6 h-6 text-white" data-id="element-4900" data-mp="2xdlopoq1jfwh" />
              </div>
              <h1 className="text-2xl font-bold text-white" data-id="element-4901" data-mp="2xbhbgg6954jj">Productos</h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-4902" data-mp="3c3sxfcq8xkjz">
              Gestión de inventario y ventas de productos
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-4903" data-mp="2jldwma9pcd44">
            {/* Catalog exports */}
            {activeTab === 'catalog' && canExportPdf && <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-4905" data-mp="u40ftiak3j4h" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4904" data-mp="2ekzwdeece7gn">
              
                Exportar PDF
              </Button>}
            {activeTab === 'catalog' && canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-4907" data-mp="1cw2pg9os5z58" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4906" data-mp="p3m4nrdnrt28">
              
                Exportar Excel
              </Button>}

            {/* Sales exports */}
            {activeTab === 'sales' && <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-4909" data-mp="3on6lqrk0i5x5" />} onClick={() => setIsSalesPdfModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4908" data-mp="3okkgiyklg2i3">
              
                Exportar PDF
              </Button>}
            {activeTab === 'sales' && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-4911" data-mp="1pc2eip56irf6" />} onClick={() => setIsSalesExcelModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4910" data-mp="1pdvk014235ll">
              
                Exportar Excel
              </Button>}

            {/* Stats exports */}
            {activeTab === 'stats' && <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-4913" data-mp="11gg6kx4zntj3" />} onClick={() => setIsStatsPdfModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4912" data-mp="16g2i925651ps">
              
                Exportar PDF
              </Button>}
            {activeTab === 'stats' && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-4915" data-mp="38cl671l2vc3y" />} onClick={() => setIsStatsExcelModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4914" data-mp="34a6poo4vsofu">
              
                Exportar Excel
              </Button>}

            {/* Action button */}
            {activeTab === 'catalog' ? <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-4917" data-mp="2pdpbzq97yxq4" />} onClick={() => handleOpenProductModal()} className="bg-white text-orange-700 hover:bg-orange-50 shadow-lg shadow-orange-900/20" data-id="element-4916" data-mp="2legjspttv7wo">
              
                Nuevo Producto
              </Button> : <Button leftIcon={<ShoppingCartIcon className="w-4 h-4" data-id="element-4919" data-mp="9ncks077on12" />} onClick={handleOpenSaleModal} className="bg-white text-orange-700 hover:bg-orange-50 shadow-lg shadow-orange-900/20" data-id="element-4918" data-mp="2za280us8lno1">
              
                Registrar Venta
              </Button>}
          </div>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-4920" data-mp="vpefreojs6ib" />

      {/* CATALOG TAB */}
      {activeTab === 'catalog' && <Card padding="none" data-id="element-4921" data-mp="19h2vdry09d41">
          {/* ── LOW STOCK BANNER ─────────────────────────────────────────── */}
          {(() => {
        const lowStockItems = products.filter(p => p.stock <= (p.minStock ?? 5) && p.status as any !== 'archived');
        if (lowStockItems.length === 0) return null;
        const outOfStock = lowStockItems.filter(p => p.stock === 0);
        const lowOnly = lowStockItems.filter(p => p.stock > 0);
        const previewItems = lowStockItems.slice(0, 8);
        const extraCount = lowStockItems.length - 8;
        return <div className="mx-3 sm:mx-4 mt-3 sm:mt-4 rounded-2xl overflow-hidden" style={{
          background: 'linear-gradient(135deg, #fff7ed 0%, #fff 60%)',
          border: '1px solid #fed7aa'
        }} data-id="element-4922" data-mp="3p75rxh40wsh2">
              
                {/* Header */}
                <button onClick={() => {
            if (lowStockFilter) {
              setLowStockFilter(false);
            } else {
              setLowStockFilter(true);
              setStockBannerExpanded(false);
              setTimeout(() => {
                document.getElementById('catalog-search')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest'
                });
              }, 100);
            }
          }} className="w-full px-4 py-3 text-left transition-colors hover:bg-orange-50/50" style={{
            borderBottom: '1px solid #ffedd5',
            background: 'none',
            cursor: 'pointer'
          }} data-id="element-4923" data-mp="1vx33rtc9wli6">
                
                  {/* Top row: icon + title + arrow */}
                  <div className="flex items-center gap-3" data-id="element-4924" data-mp="mwed79yhuda8">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{
                background: 'linear-gradient(135deg, #f97316, #ef4444)'
              }} data-id="element-4925" data-mp="qxhrvt4t239v">
                    
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" data-id="element-4926" data-mp="15mdz5nz1fc9f">
                      
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" data-id="element-4927" data-mp="1emj4ztol22lh" />
                      
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" data-id="element-4928" data-mp="38vprexfdae8e" />
                      
                      </svg>
                    </div>
                    <p className="text-sm font-bold text-gray-800 flex-1" data-id="element-4929" data-mp="2z30hw83dnafp">
                      Alerta de inventario
                    </p>
                    <svg className="w-4 h-4 text-orange-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" data-id="element-4930" data-mp="3ruagkjxp79je">
                    
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" data-id="element-4931" data-mp="3du9fp4hfo23x" />
                    
                    </svg>
                  </div>

                  {/* Bottom row: subtitle + badges */}
                  <div className="flex items-center justify-between gap-2 mt-1.5 pl-11" data-id="element-4932" data-mp="22h6utx8awqdy">
                    <p className="text-xs text-gray-500 flex-1 min-w-0 truncate" data-id="element-4933" data-mp="23x2onnmax97e">
                      {lowStockFilter ? '✓ Mostrando solo stock bajo — toca para quitar' : 'Toca para filtrar productos críticos'}
                    </p>
                    <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-4934" data-mp="1yvvbta5axjtv">
                      {outOfStock.length > 0 && <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold text-white" style={{
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)'
                }} data-id="element-4935" data-mp="1l3ox7shsrkwj">
                      
                          <span className="w-1.5 h-1.5 rounded-full bg-white/70 animate-pulse" data-id="element-4936" data-mp="1275wnrl275bl" />
                          {outOfStock.length} agotados
                        </div>}
                      {lowOnly.length > 0 && <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold" style={{
                  background: '#fff7ed',
                  color: '#c2410c',
                  border: '1px solid #fed7aa'
                }} data-id="element-4937" data-mp="nl064gyhc673">
                      
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400" data-id="element-4938" data-mp="16it7vfxdaa2w" />
                          {lowOnly.length} bajo
                        </div>}
                    </div>
                  </div>
                </button>

                {/* Preview chips row */}
                <div className="px-3 sm:px-4 pt-3 pb-1 flex gap-2 overflow-x-auto" style={{
            scrollbarWidth: 'none'
          }} data-id="element-4939" data-mp="1ba1igfafo1hn">
                
                  {previewItems.map(p => <div key={p.id} className="flex items-center gap-2 rounded-xl flex-shrink-0 text-xs font-medium" style={{
              padding: '6px 12px 6px 8px',
              background: p.stock === 0 ? '#fef2f2' : '#fff7ed',
              border: `1px solid ${p.stock === 0 ? '#fecaca' : '#fed7aa'}`,
              color: p.stock === 0 ? '#dc2626' : '#c2410c'
            }} data-id="element-4940" data-mp="1kbsl1b9ofs7a">
                  
                      <span className="w-5 h-5 rounded-lg flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0" style={{
                background: p.stock === 0 ? '#ef4444' : '#f97316',
                minWidth: '20px'
              }} data-id="element-4941" data-mp="2vsvirmdr8bwb">
                    
                        {p.stock}
                      </span>
                      <span className="truncate" style={{
                maxWidth: '140px'
              }} data-id="element-4942" data-mp="k1huxchtxpwl">
                    
                        {p.name}
                      </span>
                    </div>)}
                  <div className="flex-shrink-0 w-2" data-id="element-4943" data-mp="u79anulmvum8" />
                </div>
              </div>;
      })()}

          <div id="catalog-search" className="p-4 border-b border-gray-200 dark:border-gray-700" data-id="element-4944" data-mp="pe6que6nrzj8">
          
            <div className="flex items-center gap-3 flex-wrap" data-id="element-4945" data-mp="lbtntxjaqfq5">
              <div className="relative flex-1 min-w-[200px] max-w-md" data-id="element-4946" data-mp="gfuurwn0w9je">
                <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-4947" data-mp="bf73raysnsp6" />
                <Input style={{
              paddingLeft: '2.5rem'
            }} placeholder="Buscar por nombre, SKU o categoría..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="" data-id="element-4948" data-mp="6hezrikvkg4m" />
              
              </div>
              {lowStockFilter && <button onClick={() => setLowStockFilter(false)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80" style={{
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            color: '#c2410c',
            cursor: 'pointer'
          }} data-id="element-4949" data-mp="3qba4zlgligah">
              
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400" data-id="element-4950" data-mp="cxyow6u501y3" />
                  Stock bajo activo
                  <svg className="w-3 h-3 ml-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" data-id="element-4951" data-mp="hup6wzpi57m5">
                
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" data-id="element-4952" data-mp="8th9skyq2ctn" />
                
                  </svg>
                </button>}
            </div>
          </div>

          {/* ── MOBILE CARDS ── */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4953" data-mp="2l2pix9v799d4">
            {paginatedProducts.length === 0 ? <div className="py-8 text-center text-gray-400 text-sm" data-id="element-4954" data-mp="2v2os98zg13r1">
                No se encontraron productos
              </div> : paginatedProducts.map(product => <div key={product.id} className="px-4 py-3 flex items-center gap-3" data-id="element-4955" data-mp="2q1ixz6q4x8fe">
            
                  <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400 flex-shrink-0" data-id="element-4956" data-mp="1xf7v1pf22vff">
                    <PackageIcon className="w-4 h-4" data-id="element-4957" data-mp="2c5x68rsioaja" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4958" data-mp="27bj6yeaqhj9c">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-4959" data-mp="1jn01e6j2jkky">
                      {product.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-4960" data-mp="1emccpribbftw">
                      {product.category} · SKU: {product.sku}
                    </p>
                    <div className="flex items-center gap-2 mt-1" data-id="element-4961" data-mp="y4zvmsgd18v7">
                      <span className="text-xs font-semibold text-gray-900 dark:text-white" data-id="element-4962" data-mp="132s00ly6ynga">
                        {formatCurrency(product.price)}
                      </span>
                      <span className={`text-xs font-medium ${product.stock <= 5 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`} data-id="element-4963" data-mp="1c3zl22bp5rsf">
                  
                        Stock: {product.stock}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-4964" data-mp="1qsw22ovqef56">
                    <button onClick={() => handleOpenProductModal(product)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" data-id="element-4965" data-mp="1qs43r6ar23b7">
                
                      <EditIcon className="w-4 h-4" data-id="element-4966" data-mp="3kc4gobdln4du" />
                    </button>
                    <button onClick={() => handleArchiveProduct(product.id)} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar producto" data-id="element-4967" data-mp="3ga9ez5rraozj">
                
                      <ArchiveIcon className="w-4 h-4" data-id="element-4968" data-mp="7u7ujoja5ijf" />
                    </button>
                    {product.status === 'active' && <button onClick={() => handleDeactivateProduct(product.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar producto" data-id="element-4969" data-mp="2uv1un5q9h8x">
                
                        <PowerOffIcon className="w-4 h-4" data-id="element-4970" data-mp="qkz0sh7z0stl" />
                      </button>}
                  </div>
                </div>)}
          </div>

          {/* ── DESKTOP TABLE ── */}
          <div className="hidden sm:block overflow-x-auto" data-id="element-4971" data-mp="13i0ncp8p5whg">
            <table className="w-full" data-id="element-4972" data-mp="yhl8tucoiixw">
              <thead data-id="element-4973" data-mp="ygjkwpake1yr">
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-4974" data-mp="yk7hjxbb34hp">
                  <SortableHeader label="Producto" sortKey="name" currentDirection={isProductSorted('name')} onSort={requestProductSort} data-id="element-4975" data-mp="tkurz4t1vjbf" />
                

                  <SortableHeader label="SKU" sortKey="sku" currentDirection={isProductSorted('sku')} onSort={requestProductSort} className="hidden sm:table-cell" data-id="element-4976" data-mp="1h842yxd2mrxx" />
                

                  <SortableHeader label="Categoría" sortKey="category" currentDirection={isProductSorted('category')} onSort={requestProductSort} className="hidden sm:table-cell" data-id="element-4977" data-mp="24y6p6wub13om" />
                

                  <SortableHeader label="Precio" sortKey="price" currentDirection={isProductSorted('price')} onSort={requestProductSort} data-id="element-4978" data-mp="3iwkss8wtmfxq" />
                

                  <SortableHeader label="Stock" sortKey="stock" currentDirection={isProductSorted('stock')} onSort={requestProductSort} data-id="element-4979" data-mp="p4s3886bv1s" />
                

                  <SortableHeader label="Estado" sortKey="status" currentDirection={isProductSorted('status')} onSort={requestProductSort} data-id="element-4980" data-mp="alhx32965vbu" />
                

                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4981" data-mp="jh7cmunxrvhb">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4982" data-mp="tdk8nz6yelf8">
                {loading ? <tr data-id="element-4983" data-mp="2pechr0ke8pbo">
                    <td colSpan={7} className="py-8 text-center text-gray-400" data-id="element-4984" data-mp="2khm1od8l9tq9">
                      Cargando productos...
                    </td>
                  </tr> : paginatedProducts.length === 0 ? <tr data-id="element-4985" data-mp="2uf059c9eyo15">
                    <td colSpan={7} className="py-8 text-center text-gray-500" data-id="element-4986" data-mp="2ueqevnkgieux">
                      No se encontraron productos
                    </td>
                  </tr> : paginatedProducts.map(product => <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-4987" data-mp="3451rdyi7hwdf">
                
                      <td className="py-3 px-4" data-id="element-4988" data-mp="3d5hxzgu8xjmi">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-4989" data-mp="3d6swb14x9z68">
                          {product.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate max-w-[200px]" data-id="element-4990" data-mp="1isidb9b0dz8d">
                          {product.description}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300 hidden sm:table-cell" data-id="element-4991" data-mp="38rejcoglcwx">
                        {product.sku}
                      </td>
                      <td className="py-3 px-4 hidden sm:table-cell" data-id="element-4992" data-mp="3knoe4vhyj3sh">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-4993" data-mp="3pn125jkkegwn">
                          <TagIcon className="w-3 h-3" data-id="element-4994" data-mp="3goyrecb149w6" />
                          {product.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white" data-id="element-4995" data-mp="36pric1bbv1ev">
                        {formatCurrency(product.price)}
                      </td>
                      <td className="py-3 px-4" data-id="element-4996" data-mp="10036b9ag8x4a">
                        {product.stock <= (product.minStock ?? 5) ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${product.stock === 0 ? 'bg-red-600 text-white' : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700'}`} data-id="element-4997" data-mp="14w0l3b62dvey">
                    
                            {product.stock}
                          </span> : <span className="font-medium text-gray-900 dark:text-white" data-id="element-4998" data-mp="2n1i55frxy8ey">
                            {product.stock}
                          </span>}
                      </td>
                      <td className="py-3 px-4" data-id="element-4999" data-mp="2d9u6att21p3g">
                        <Badge variant={product.status === 'active' ? 'success' : 'default'} data-id="element-5000" data-mp="299k3fq0k7est">
                    
                          {product.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-5001" data-mp="1uedtwt298xuq">
                        <div className="flex justify-end gap-2" data-id="element-5002" data-mp="1k9e69bsua8qm">
                          <button onClick={() => handleOpenProductModal(product)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar producto" data-id="element-5003" data-mp="1kb8j02tyiujm">
                      
                            <EditIcon className="w-4 h-4" data-id="element-5004" data-mp="1z5n203diu3ya" />
                          </button>
                          <button onClick={() => handleArchiveProduct(product.id)} className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Archivar producto" data-id="element-5005" data-mp="11khdjcd3boai">
                      
                            <ArchiveIcon className="w-4 h-4" data-id="element-5006" data-mp="1g8vetiuuz4oy" />
                          </button>
                          {product.status === 'active' && <button onClick={() => handleDeactivateProduct(product.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Desactivar producto" data-id="element-5007" data-mp="nqq0hu05hma1">
                      
                              <PowerOffIcon className="w-4 h-4" data-id="element-5008" data-mp="pflgkxzi2c9b" />
                            </button>}
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-5009" data-mp="3nzaga6uesf6t">
            <Pagination currentPage={productPage} totalPages={productTotalPages} totalItems={productTotalItems} startIndex={productStartIndex} endIndex={productEndIndex} onPageChange={productGoToPage} onNext={productNextPage} onPrev={productPrevPage} data-id="element-5010" data-mp="6q5c5zkp1znm" />
          
          </div>
        </Card>}

      {/* SALES TAB */}
      {activeTab === 'sales' && <Card padding="none" data-id="element-5011" data-mp="1krte0q47jvis">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-3" data-id="element-5012" data-mp="1pp30bwwavr2e">
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-5013" data-mp="1bpacqag2lqyn">
              <div className="relative flex-1" data-id="element-5014" data-mp="1bqvasa1lfs19">
                <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-5015" data-mp="1fplyjqk65xbp" />
                <Input style={{
              paddingLeft: '2.5rem'
            }} placeholder="Buscar por cliente o vendedor..." value={saleSearch} onChange={e => setSaleSearch(e.target.value)} className="" data-id="element-5016" data-mp="23ghumzvnreor" />
              
              </div>
              <div className="w-full sm:w-48" data-id="element-5017" data-mp="2meu64sa1l0ni">
                <Select options={[{
              value: '',
              label: 'Todos los vendedores'
            }, ...sellers.map(s => ({
              value: s.id,
              label: s.name
            }))]} value={saleSeller} onChange={setSaleSeller} placeholder="Vendedor" data-id="element-5018" data-mp="2mdrdofx2mq5h" />
              
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-5019" data-mp="1nqieodbussco">
              <input type="date" value={saleDateFrom} onChange={e => setSaleDateFrom(e.target.value)} className="flex-1 min-w-0 w-0 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400" data-id="element-5020" data-mp="1nnvyjpawd4iu" />
            
              <span className="text-gray-400 flex-shrink-0 font-medium" data-id="element-5021" data-mp="26iujr7tw67qi">→</span>
              <input type="date" value={saleDateTo} onChange={e => setSaleDateTo(e.target.value)} className="flex-1 min-w-0 w-0 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400" data-id="element-5022" data-mp="zybdn0v6nx6x" />
            
            </div>
          </div>

          {/* ── MOBILE CARDS VENTAS ── */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-5023" data-mp="3levq3gns872e">
            {paginatedSales.length === 0 ? <div className="py-8 text-center text-gray-400 text-sm" data-id="element-5024" data-mp="3qf05t4gv8xrk">
                No se encontraron ventas
              </div> : paginatedSales.map(sale => {
          const patient = patients.find(p => p.id === sale.patientId);
          const saleItems = sale.items || [];
          return <div key={sale.id} className="px-4 py-3 flex items-start gap-3" data-id="element-5025" data-mp="24zksmnm345wh">
                
                    <div className="flex-1 min-w-0" data-id="element-5026" data-mp="29z71ylysl200">
                      <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-5027" data-mp="1vk7hvsm7jxh3">
                        {patient?.name || sale.patientName || 'Cliente'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5028" data-mp="1lm1ibhnvj3fe">
                        {sale.date} · {sale.createdByUserName || ''}
                      </p>
                      <p className="text-xs text-gray-400 truncate" data-id="element-5029" data-mp="1qj085o0i8o7s">
                        {saleItems.map((i: any) => {
                  const name = i.productName || '';
                  const disc = i.discount ? ` (-${i.discount}%)` : '';
                  return `${i.quantity || 1}x ${name}${disc}`;
                }).join(', ')}
                      </p>
                      <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 mt-1" data-id="element-5030" data-mp="sxcdb7jp6dyd">
                        {formatCurrency(sale.total)}
                      </p>
                    </div>
                    {canEditSales && <button onClick={() => handleOpenEditSaleModal(sale)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors flex-shrink-0" title="Editar venta" data-id="element-5031" data-mp="3c35az8r8n4ce">
                  
                        <EditIcon className="w-4 h-4" data-id="element-5032" data-mp="3h4mb321wemrx" />
                      </button>}
                  </div>;
        })}
          </div>

          {/* ── DESKTOP TABLE VENTAS ── */}
          <div className="hidden sm:block overflow-x-auto" data-id="element-5033" data-mp="2og6xmjyf0cip">
            <table className="w-full" data-id="element-5034" data-mp="326k56l7e9p7i">
              <thead data-id="element-5035" data-mp="327lf2mcoc5nn">
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-5036" data-mp="3284wm1yews15">
                  <SortableHeader label="Fecha" sortKey="date" currentDirection={isSaleSorted('date')} onSort={requestSaleSort} data-id="element-5037" data-mp="29ppwz2tltqmv" />
                

                  <SortableHeader label="Cliente/Paciente" sortKey="patientId" currentDirection={isSaleSorted('patientId')} onSort={requestSaleSort} data-id="element-5038" data-mp="8o1jlmrwhykh" />
                

                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-5039" data-mp="idthoqz7l9tp">
                    Productos
                  </th>
                  <SortableHeader label="Total" sortKey="total" currentDirection={isSaleSorted('total')} onSort={requestSaleSort} data-id="element-5040" data-mp="3lua2dvkoy3w1" />
                

                  <SortableHeader label="Vendedor" sortKey="createdByUserName" currentDirection={isSaleSorted('createdByUserName')} onSort={requestSaleSort} data-id="element-5041" data-mp="3vr6aw1alaq0l" />
                

                  {canEditSales && <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-5042" data-mp="1g0k1vaxoxegx">
                      Acciones
                    </th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5043" data-mp="2vl2htdd3bvz4">
                {loading ? <tr data-id="element-5044" data-mp="2qs9gkzgtz5rs">
                    <td colSpan={canEditSales ? 6 : 5} className="py-8 text-center text-gray-400" data-id="element-5045" data-mp="2qrgh8jk7bijo">
                  
                      Cargando ventas...
                    </td>
                  </tr> : paginatedSales.length === 0 ? <tr data-id="element-5046" data-mp="2csqf2piiysog">
                    <td colSpan={canEditSales ? 6 : 5} className="py-8 text-center text-gray-500" data-id="element-5047" data-mp="2cvmigtd569ka">
                  
                      No se encontraron ventas
                    </td>
                  </tr> : paginatedSales.map(sale => {
              const patient = patients.find(p => p.id === sale.patientId);
              return <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-5048" data-mp="2uvfgyardxwor">
                    
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-white" data-id="element-5049" data-mp="2yvg6aqg0vsal">
                          {sale.date}
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white" data-id="element-5050" data-mp="2pw2uhr4fermp">
                          {patient?.name || 'Desconocido'}
                        </td>
                        <td className="py-3 px-4" data-id="element-5051" data-mp="2fyg4a8hofdnf">
                          <div className="flex flex-col gap-1" data-id="element-5052" data-mp="2fzzh1p44j6kg">
                            {sale.items.map((item, idx) => {
                      const product = products.find(p => p.id === item.productId);
                      const hasDiscount = (item as any).discount > 0;
                      return <span key={idx} className="text-xs text-gray-600 dark:text-gray-300" data-id="element-5053" data-mp="1x6vm80f66no9" data-mpi={idx}>
                              
                                  {item.quantity}x{' '}
                                  {product?.name || item.productName || 'Producto eliminado'}
                                  {hasDiscount && <span className="ml-1 text-emerald-600 dark:text-emerald-400 font-medium" data-id="element-5054" data-mp="1wl9aqgvh3uvf">
                                      (-{(item as any).discount}%)
                                    </span>}
                                </span>;
                    })}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-bold text-orange-600 dark:text-orange-400" data-id="element-5055" data-mp="21e2h196lbb9m">
                          {formatCurrency(sale.total)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500 flex items-center gap-1.5 mt-1" data-id="element-5056" data-mp="2bbqc6083yksb">
                          <UserIcon className="w-3.5 h-3.5" data-id="element-5057" data-mp="2iey6ihcgdwtl" />
                          {sale.createdByUserName}
                        </td>
                        {canEditSales && <td className="py-3 px-4 text-right" data-id="element-5058" data-mp="2sai02x3jojf6">
                            <button onClick={() => handleOpenEditSaleModal(sale)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar venta" data-id="element-5059" data-mp="2x1ztn038nhq4">
                        
                              <EditIcon className="w-4 h-4" data-id="element-5060" data-mp="3b2ala5cdh0cc" />
                            </button>
                          </td>}
                      </tr>;
            })}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-5061" data-mp="1gpuff5nhno80">
            <Pagination currentPage={salePage} totalPages={saleTotalPages} totalItems={saleTotalItems} startIndex={saleStartIndex} endIndex={saleEndIndex} onPageChange={saleGoToPage} onNext={saleNextPage} onPrev={salePrevPage} data-id="element-5062" data-mp="1lnw1lzg7o3np" />
          
          </div>
        </Card>}

      {/* ── STATS TAB ─────────────────────────────────────────────────────── */}
      {activeTab === 'stats' && canViewProductStats && <div className="space-y-6" data-id="element-5063" data-mp="1x1xqpoliiud1">
          {/* Read-only notice */}
          {statsPermLevel === 'read' && <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300" data-id="element-5064" data-mp="1s44h0ll03i80">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-5065" data-mp="1i4x7yalau9qp">
            
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" data-id="element-5066" data-mp="6oyrvmrpenfz" />
            

                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" data-id="element-5067" data-mp="11difg2bg1drs" />
            
              </svg>
              <span data-id="element-5068" data-mp="1f4djn1azz6p1">
                Estás en modo <strong data-id="element-5069" data-mp="1f5f601xgrfm2">solo lectura</strong>. Puedes consultar
                las estadísticas pero no modificar datos desde esta vista.
              </span>
            </div>}

          {/* Filters row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-5070" data-mp="16a8vwqr5ubdq">
            {/* Date filter */}
            <Card className="lg:col-span-1" data-id="element-5071" data-mp="1abu43ysr21hl">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-1.5" data-id="element-5072" data-mp="1y39ac2nnd2y4">
                <BarChart2Icon className="w-3.5 h-3.5 text-amber-500" data-id="element-5073" data-mp="22v9haf24ww47" /> Período
              </p>
              <div className="flex flex-col gap-2" data-id="element-5074" data-mp="3njlc6f2noqar">
                <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5" data-id="element-5075" data-mp="3niaacksrtsma">
                  <span className="text-xs text-gray-400 w-8 flex-shrink-0" data-id="element-5076" data-mp="140p2kk510pq">
                    Desde
                  </span>
                  <input type="date" value={statsDateFrom} onChange={e => setStatsDateFrom(e.target.value)} className="bg-transparent text-sm border-none focus:ring-0 text-gray-700 dark:text-gray-300 flex-1 min-w-0" data-id="element-5077" data-mp="3sc4yq1jbmvrp" />
                
                </div>
                <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5" data-id="element-5078" data-mp="39kvil9f1p8r5">
                  <span className="text-xs text-gray-400 w-8 flex-shrink-0" data-id="element-5079" data-mp="ts69f67a4np1">
                    Hasta
                  </span>
                  <input type="date" value={statsDateTo} onChange={e => setStatsDateTo(e.target.value)} className="bg-transparent text-sm border-none focus:ring-0 text-gray-700 dark:text-gray-300 flex-1 min-w-0" data-id="element-5080" data-mp="owz8u79b3z4c" />
                
                </div>
              </div>
            </Card>

            {/* Product multi-selector */}
            <Card className="lg:col-span-2 overflow-hidden" padding="none" data-id="element-5081" data-mp="3b8bt93i8yuq8">
              <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between" data-id="element-5082" data-mp="3b8kat8pbeo7r">
                <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1.5" data-id="element-5083" data-mp="367edexkpmcrw">
                  <PackageIcon className="w-3.5 h-3.5 text-amber-500" data-id="element-5084" data-mp="3f9xdxb10wdaz" />
                  Filtrar productos
                  {selectedStatProducts.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" data-id="element-5085" data-mp="2ris0t3mgvz4d">
                      {selectedStatProducts.length} seleccionado
                      {selectedStatProducts.length > 1 ? 's' : ''}
                    </span>}
                </p>
                <div className="flex items-center gap-2" data-id="element-5086" data-mp="23h54wnfnexf1">
                  <button onClick={() => setSelectedStatProducts(products.filter(p => p.status as any !== 'archived').map(p => p.id))} className="text-xs text-amber-600 dark:text-amber-400 hover:underline" data-id="element-5087" data-mp="23eilnqjeatgz">
                  
                    Todos
                  </button>
                  <span className="text-gray-300 text-xs" data-id="element-5088" data-mp="2r5oct1usd86l">·</span>
                  <button onClick={() => setSelectedStatProducts([])} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:underline" data-id="element-5089" data-mp="2r8aievy49dm6">
                  
                    Limpiar
                  </button>
                </div>
              </div>
              {/* Search */}
              <div className="px-3 pt-3 pb-2" data-id="element-5090" data-mp="3decxd2qcb3q8">
                <div className="relative" data-id="element-5091" data-mp="3dgppg8ptauh0">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" data-id="element-5092" data-mp="38ncznv9echmo" />
                  <input type="text" placeholder="Buscar producto..." value={productSelectorSearch} onChange={e => setProductSelectorSearch(e.target.value)} className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400" data-id="element-5093" data-mp="33pl852cnyxj0" />
                
                </div>
              </div>
              {/* Product chips */}
              <div className="px-3 pb-3 max-h-36 overflow-y-auto flex flex-wrap gap-2" data-id="element-5094" data-mp="nx5l2bmtnzhb">
                {products.filter(p => p.status as any !== 'archived').filter(p => p.name.toLowerCase().includes(productSelectorSearch.toLowerCase())).map(p => {
              const selected = selectedStatProducts.includes(p.id);
              return <button key={p.id} onClick={() => setSelectedStatProducts(selected ? selectedStatProducts.filter(id => id !== p.id) : [...selectedStatProducts, p.id])} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${selected ? 'bg-amber-500 border-amber-500 text-white shadow-sm shadow-amber-300/30' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-amber-400 hover:text-amber-600'}`} data-id="element-5095" data-mp="157j8n2d6jf05">
                    
                        {selected && <span className="text-[10px]" data-id="element-5096" data-mp="1vhihsbfymkdu">✓</span>}
                        {p.name}
                      </button>;
            })}
                {products.filter(p => p.status as any !== 'archived').filter(p => p.name.toLowerCase().includes(productSelectorSearch.toLowerCase())).length === 0 && <p className="text-xs text-gray-400 italic py-2" data-id="element-5097" data-mp="2eebbg8t1hn8p">
                    Sin resultados
                  </p>}
              </div>
              <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800" data-id="element-5098" data-mp="2o67c9rmifrgm">
                <p className="text-[11px] text-gray-400" data-id="element-5099" data-mp="2j62vs1llqwqe">
                  {selectedStatProducts.length === 0 ? 'Mostrando todos los productos. Selecciona uno o varios para filtrar.' : `Mostrando estadísticas de ${selectedStatProducts.length} producto${selectedStatProducts.length > 1 ? 's' : ''}.`}
                </p>
              </div>
            </Card>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-5100" data-mp="3gquqizyzw331">
            {[{
          label: 'Ingresos totales',
          value: formatCurrency(totalStatsRevenue),
          icon: <DollarSignIcon className="w-5 h-5" data-id="element-5101" data-mp="37q69dweb1mlx" />,
          color: 'from-amber-500 to-orange-500',
          sub: `${productStats.length} producto${productStats.length !== 1 ? 's' : ''} vendido${productStats.length !== 1 ? 's' : ''}`
        }, {
          label: 'Unidades vendidas',
          value: totalStatsUnits.toLocaleString(),
          icon: <BoxIcon className="w-5 h-5" data-id="element-5102" data-mp="2p0q651b8jg1t" />,
          color: 'from-blue-500 to-indigo-500',
          sub: `${sales.filter(s => (!statsDateFrom || s.date >= statsDateFrom) && (!statsDateTo || s.date <= statsDateTo)).length} ventas`
        }, {
          label: 'Ganancia estimada',
          value: formatCurrency(totalStatsProfit),
          icon: totalStatsProfit >= 0 ? <TrendingUpIcon className="w-5 h-5" data-id="element-5103" data-mp="1r6x8wks9ykkm" /> : <TrendingDownIcon className="w-5 h-5" data-id="element-5104" data-mp="1r5dsmu6mc9iu" />,
          color: totalStatsProfit >= 0 ? 'from-emerald-500 to-green-500' : 'from-red-500 to-rose-500',
          sub: 'Ingresos − Costo'
        }].map(kpi => <div key={kpi.label} className="relative overflow-hidden rounded-2xl p-5 text-white shadow-lg" data-id="element-5105" data-mp="2ew01gnog5xtw">
            
                <div className={`absolute inset-0 bg-gradient-to-br ${kpi.color}`} data-id="element-5106" data-mp="29w3ymrrhkcfx" />
            

                <div className="relative z-10" data-id="element-5107" data-mp="2000vpjot89rx">
                  <div className="flex items-center justify-between mb-3" data-id="element-5108" data-mp="1zz8yy5jh8602">
                    <p className="text-sm font-medium text-white/80" data-id="element-5109" data-mp="tjyjmv3wwewr">
                      {kpi.label}
                    </p>
                    <div className="p-2 bg-white/20 rounded-xl" data-id="element-5110" data-mp="oj28pxl57shu">{kpi.icon}</div>
                  </div>
                  <p className="text-2xl font-bold tracking-tight" data-id="element-5111" data-mp="309s688ehbzrr">
                    {kpi.value}
                  </p>
                  <p className="text-xs text-white/70 mt-1" data-id="element-5112" data-mp="354pc56ggpt0f">{kpi.sub}</p>
                </div>
              </div>)}
          </div>

          {/* Revenue trend */}
          <Card padding="none" className="overflow-hidden" data-id="element-5113" data-mp="3eyfx41ddrlbd">
            <div className="p-4 border-b border-gray-100 dark:border-gray-800" data-id="element-5114" data-mp="3jwhjav63s0r2">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-5115" data-mp="3nx9q7hri5tk6">
                <TrendingUpIcon className="w-4 h-4 text-amber-500" data-id="element-5116" data-mp="3sww31q3f57sg" />
                Tendencia de ingresos mensual
              </h3>
            </div>
            <div className="p-4" data-id="element-5117" data-mp="1ptd11zbh7jt">
              {revenueTrend.length < 2 ? <div className="py-10 text-center text-sm text-gray-400 italic" data-id="element-5118" data-mp="1yjeqsz8grxcq">
                  Amplía el rango de fechas para ver la tendencia.
                </div> : <ResponsiveContainer width="100%" height={220} data-id="element-5119" data-mp="99uhm4xid1ym">
                  <LineChart data={revenueTrend} margin={{
              left: 0,
              right: 8,
              top: 4
            }} data-id="element-5120" data-mp="981bcqqt4jr5">
                
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" data-id="element-5121" data-mp="j1qghi7f4w3w" />
                    <XAxis dataKey="month" fontSize={12} stroke="#9ca3af" tickLine={false} axisLine={false} data-id="element-5122" data-mp="e12sl5ehkvdw" />
                

                    <YAxis fontSize={11} stroke="#9ca3af" tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-5123" data-mp="3hj4902y0refo" />
                

                    <Tooltip contentStyle={{
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.10)',
                padding: '10px 14px'
              }} formatter={(v: number) => [formatCurrency(v), 'Ingresos']} data-id="element-5124" data-mp="1bnjme0o0gquw" />
                

                    <Line type="monotone" dataKey="amount" stroke="#f59e0b" strokeWidth={2.5} dot={{
                fill: '#f59e0b',
                r: 4
              }} activeDot={{
                r: 6
              }} data-id="element-5125" data-mp="1j5hkza4nmhjc" />
                
                  </LineChart>
                </ResponsiveContainer>}
            </div>
          </Card>

          {/* Bar chart – top 5 products */}
          {productStats.length > 0 && <Card padding="none" className="overflow-hidden" data-id="element-5126" data-mp="1s6z6j80tzbff">
              <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between" data-id="element-5127" data-mp="2fxl2i0xk9592">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-5128" data-mp="2kpuzu21097ld">
                  <BarChart2Icon className="w-4 h-4 text-amber-500" data-id="element-5129" data-mp="3m4s9eefhn5yl" />
                  Ingresos por producto
                </h3>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400" data-id="element-5130" data-mp="3q5lrgr691qjk">
                  Top 5 más vendidos
                </span>
              </div>
              <div className="p-4" data-id="element-5131" data-mp="336z5p9x1is05">
                <ResponsiveContainer width="100%" height={Math.max(220, top5ProductStats.length * 52)} data-id="element-5132" data-mp="3380q44zy4quj">
              
                  <BarChart data={top5ProductStats} layout="vertical" margin={{
              left: 0,
              right: 50
            }} data-id="element-5133" data-mp="37aduoq2yiipq">
                
                    <defs data-id="element-5134" data-mp="rhomoq6xg3p7">
                      <linearGradient id="prodBarGrad" x1="0" y1="0" x2="1" y2="0" data-id="element-5135" data-mp="rgdb4478prnj">
                    
                        <stop offset="0%" stopColor="#f59e0b" data-id="element-5136" data-mp="2gx8tpu05xxcm" />
                        <stop offset="100%" stopColor="#ea580c" data-id="element-5137" data-mp="2bzqvoynop2je" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal vertical={false} data-id="element-5138" data-mp="277pcess3mbjv" />
                

                    <XAxis type="number" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-5139" data-mp="1y93tgwipsuik" />
                

                    <YAxis dataKey="name" type="category" width={140} fontSize={12} tickLine={false} axisLine={false} tick={{
                fill: '#374151',
                fontWeight: 500
              }} data-id="element-5140" data-mp="1jbl88458wzfv" />
                

                    <Tooltip contentStyle={{
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.10)',
                padding: '10px 14px'
              }} formatter={(v: number) => [formatCurrency(v), 'Ingresos']} data-id="element-5141" data-mp="1bxu2dtbx3uuu" />
                

                    <Bar dataKey="revenue" fill="url(#prodBarGrad)" radius={[0, 6, 6, 0]} label={{
                position: 'right',
                fontSize: 11,
                fill: '#6b7280',
                formatter: (v: number) => formatCurrency(v)
              }} data-id="element-5142" data-mp="1uqo95zze0gnz" />
                
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>}

          {/* Detail table */}
          <Card padding="none" className="overflow-hidden" data-id="element-5143" data-mp="e9sfzdzn2qim">
            <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between" data-id="element-5144" data-mp="6r2myx14x85h">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-5145" data-mp="1srasfndalk3">
                <PackageIcon className="w-4 h-4 text-amber-500" data-id="element-5146" data-mp="gq1olvnhg3iz" />
                Detalle por producto
              </h3>
              <span className="text-xs text-gray-400" data-id="element-5147" data-mp="bpma30rgspzf">
                {productStats.length} productos
              </span>
            </div>
            {productStats.length === 0 ? <div className="py-14 text-center text-gray-400 text-sm italic" data-id="element-5148" data-mp="3fa0f2ub9ghn8">
                No hay ventas para los filtros seleccionados.
              </div> : <>
                {/* ── MOBILE CARDS ── */}
                <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-5149" data-mp="3p4ijk1oi4h83">
                  {paginatedProductStats.map((p, i) => {
              const profit = p.revenue - p.cost;
              const pct = totalStatsRevenue > 0 ? p.revenue / totalStatsRevenue * 100 : 0;
              return <div key={p.id} className="px-4 py-3 space-y-2" data-id="element-5150" data-mp="23sfmz00sp9z9" data-mpi={i}>
                        <div className="flex items-start justify-between gap-2" data-id="element-5151" data-mp="28qqvn7fctefw">
                          <div className="flex-1 min-w-0" data-id="element-5152" data-mp="2dhfpuuif4piq">
                            <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-5153" data-mp="2dj0jmhwws4fj">
                              {p.name}
                            </p>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 mt-1" data-id="element-5154" data-mp="2nekaur0j2exy">
                              <TagIcon className="w-3 h-3" data-id="element-5155" data-mp="2wfijjmlwvamv" />
                              {p.category}
                            </span>
                          </div>
                          <p className="text-sm font-bold text-amber-600 dark:text-amber-400 whitespace-nowrap" data-id="element-5156" data-mp="122agx8rp1uqo">
                            {formatCurrency(p.revenue)}
                          </p>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs" data-id="element-5157" data-mp="pr9rw01klaq9">
                          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-2 py-1.5 text-center" data-id="element-5158" data-mp="ksoro0mbir1t">
                            <p className="text-gray-400" data-id="element-5159" data-mp="fun6na5c0hnp">Unidades</p>
                            <p className="font-bold text-gray-700 dark:text-gray-200" data-id="element-5160" data-mp="fsakeksjjl4u">
                              {p.units}
                            </p>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-2 py-1.5 text-center" data-id="element-5161" data-mp="18gzmam1f7uhn">
                            <p className="text-gray-400" data-id="element-5162" data-mp="13lk81szi3wkp">Ventas</p>
                            <p className="font-bold text-gray-700 dark:text-gray-200" data-id="element-5163" data-mp="zhkv1kdfjfw3">
                              {p.salesCount}
                            </p>
                          </div>
                          <div className={`rounded-lg px-2 py-1.5 text-center ${profit >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-red-50 dark:bg-red-900/20'}`} data-id="element-5164" data-mp="3k6s6mgt1zn7d">
                        
                            <p className="text-gray-400" data-id="element-5165" data-mp="3qmiv8lty9fr5">Ganancia</p>
                            <p className={`font-bold text-xs ${profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} data-id="element-5166" data-mp="3bqjgiscc4hd1">
                          
                              {profit >= 0 ? '+' : ''}
                              {formatCurrency(profit)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-5167" data-mp="2t1weyjtdaa47">
                          <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-1.5 overflow-hidden" data-id="element-5168" data-mp="2xzocpugp0qgb">
                            <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full" style={{
                      width: `${pct}%`
                    }} data-id="element-5169" data-mp="2jzwutin22px1" />
                        
                          </div>
                          <span className="text-xs text-gray-400 w-10 text-right" data-id="element-5170" data-mp="20f4w0rb5svgg">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      </div>;
            })}
                  {/* Mobile total row */}
                  <div className="px-4 py-3 bg-amber-50/50 dark:bg-amber-900/10 border-t-2 border-gray-200 dark:border-gray-700" data-id="element-5171" data-mp="2oc284k8pj346">
                    <div className="flex items-center justify-between mb-2" data-id="element-5172" data-mp="2jcfy0jo6e2zl">
                      <span className="text-sm font-bold text-gray-700 dark:text-gray-300" data-id="element-5173" data-mp="2x55niy2wxq1g">
                        TOTAL GENERAL
                      </span>
                      <span className="text-sm font-bold text-amber-600 dark:text-amber-400" data-id="element-5174" data-mp="2t554t18s6sol">
                        {formatCurrency(totalStatsRevenue)}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs" data-id="element-5175" data-mp="321w2tj6w8hlt">
                      <div className="bg-white dark:bg-gray-800 rounded-lg px-2 py-1.5 text-center" data-id="element-5176" data-mp="da8t8sgipk71">
                        <p className="text-gray-400" data-id="element-5177" data-mp="hak97rxxqwc6">Unidades</p>
                        <p className="font-bold text-gray-700 dark:text-gray-200" data-id="element-5178" data-mp="mb6syyjxb5jt">
                          {totalStatsUnits}
                        </p>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg px-2 py-1.5 text-center" data-id="element-5179" data-mp="3psp3jdr2yqnx">
                        <p className="text-gray-400" data-id="element-5180" data-mp="3uqqmzzsf4rzx">Ventas</p>
                        <p className="font-bold text-gray-700 dark:text-gray-200" data-id="element-5181" data-mp="3up719tnztoqr">
                          {productStats.reduce((s, p) => s + p.salesCount, 0)}
                        </p>
                      </div>
                      <div className={`rounded-lg px-2 py-1.5 text-center ${totalStatsProfit >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-red-50 dark:bg-red-900/20'}`} data-id="element-5182" data-mp="371p75rh9aekm">
                    
                        <p className="text-gray-400" data-id="element-5183" data-mp="34hsudapfb1bc">Ganancia</p>
                        <p className={`font-bold ${totalStatsProfit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} data-id="element-5184" data-mp="34ewydrx4ympj">
                      
                          {totalStatsProfit >= 0 ? '+' : ''}
                          {formatCurrency(totalStatsProfit)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── DESKTOP TABLE ── */}
                <div className="hidden sm:block overflow-x-auto" data-id="element-5185" data-mp="2gq403ot1k2rr">
                  <table className="w-full" data-id="element-5186" data-mp="2brsphbux9o8h">
                    <thead data-id="element-5187" data-mp="2bqhkx9tnkcgb">
                      <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50" data-id="element-5188" data-mp="26tr60ptl3mwh">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-5189" data-mp="21uxu31ty43yf">
                          #
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-5190" data-mp="1xv6t65apg7g6">
                          Producto
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell" data-id="element-5191" data-mp="5cpltity3r9m">
                          Categoría
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-5192" data-mp="f2sefhx5n37f">
                          Unidades
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-5193" data-mp="3ik0r7nd0zcva">
                          Ventas
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-5194" data-mp="3sj00n85m7cjc">
                          Costo
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-5195" data-mp="12tejr1fry9dq">
                          Ingresos
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-5196" data-mp="2s9ryp9jpgykv">
                          Ganancia
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell" data-id="element-5197" data-mp="2ikqgrdtlo749">
                          % del total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-5198" data-mp="28odnui5vpxak">
                      {paginatedProductStats.map((p, i) => {
                  const profit = p.revenue - p.cost;
                  const pct = totalStatsRevenue > 0 ? p.revenue / totalStatsRevenue * 100 : 0;
                  const globalIndex = statsTableStartIndex + i;
                  return <tr key={p.id} className="hover:bg-amber-50/40 dark:hover:bg-amber-900/10 transition-colors" data-id="element-5199" data-mp="3p0khwg1xr6qv" data-mpi={i}>
                        
                            <td className="py-3 px-4 text-sm text-gray-400 font-medium" data-id="element-5200" data-mp="1s34ze25x54l7">
                              {globalIndex + 1}
                            </td>
                            <td className="py-3 px-4" data-id="element-5201" data-mp="1dbw1z6vryexi">
                              <p className="font-semibold text-gray-900 dark:text-white text-sm" data-id="element-5202" data-mp="1idv46dfs35sv">
                                {p.name}
                              </p>
                            </td>
                            <td className="py-3 px-4 hidden sm:table-cell" data-id="element-5203" data-mp="2b067vwyolmtw">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400" data-id="element-5204" data-mp="1w4zzx4g0zdxx">
                                <TagIcon className="w-3 h-3" data-id="element-5205" data-mp="kojrvw6svzl3" />
                                {p.category}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5206" data-mp="pls29alpizbs">
                              {p.units.toLocaleString()}
                            </td>
                            <td className="py-3 px-4 text-right text-sm text-gray-500" data-id="element-5207" data-mp="3cwlr3xrmt90d">
                              {p.salesCount}
                            </td>
                            <td className="py-3 px-4 text-right text-sm text-gray-500" data-id="element-5208" data-mp="38vry4pzc140m">
                              {formatCurrency(p.cost)}
                            </td>
                            <td className="py-3 px-4 text-right text-sm font-bold text-amber-600 dark:text-amber-400" data-id="element-5209" data-mp="2g7mds4c6xh1b">
                              {formatCurrency(p.revenue)}
                            </td>
                            <td className="py-3 px-4 text-right" data-id="element-5210" data-mp="2p5ga3f9ygaoh">
                              <span className={`text-sm font-bold ${profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} data-id="element-5211" data-mp="21gn9h5ie1enj">
                            
                                {profit >= 0 ? '+' : ''}
                                {formatCurrency(profit)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right hidden md:table-cell" data-id="element-5212" data-mp="2k3ieoi6r9r0x">
                              <div className="flex items-center justify-end gap-2" data-id="element-5213" data-mp="2otyto6q85kpe">
                                <div className="w-16 bg-gray-100 dark:bg-gray-800 rounded-full h-1.5 overflow-hidden" data-id="element-5214" data-mp="2ovs1hpckqayz">
                                  <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full" style={{
                            width: `${pct}%`
                          }} data-id="element-5215" data-mp="2xudez636uzsw" />
                              
                                </div>
                                <span className="text-xs text-gray-500 w-9 text-right" data-id="element-5216" data-mp="18h0g3wdui3z6">
                                  {pct.toFixed(1)}%
                                </span>
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                    <tfoot data-id="element-5217" data-mp="112hjqp5s1ruq">
                      <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-amber-50/50 dark:bg-amber-900/10" data-id="element-5218" data-mp="w57v3bq7pk7y">
                        <td colSpan={3} className="py-3 px-4 text-sm font-bold text-gray-700 dark:text-gray-300" data-id="element-5219" data-mp="r764m5qgiimg">
                      
                          TOTAL
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-700 dark:text-gray-300" data-id="element-5220" data-mp="1jsz7efbr5x98">
                          {totalStatsUnits.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-500" data-id="element-5221" data-mp="19yyuhra8bnpy">
                          {productStats.reduce((s, p) => s + p.salesCount, 0)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-500" data-id="element-5222" data-mp="3up7pbfh0vnu1">
                          {formatCurrency(productStats.reduce((s, p) => s + p.cost, 0))}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-amber-600 dark:text-amber-400" data-id="element-5223" data-mp="2coox5j2x0555">
                          {formatCurrency(totalStatsRevenue)}
                        </td>
                        <td className="py-3 px-4 text-right" data-id="element-5224" data-mp="22yvnwoq8w242">
                          <span className={`text-sm font-bold ${totalStatsProfit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} data-id="element-5225" data-mp="27qx60ra3gn20">
                        
                            {totalStatsProfit >= 0 ? '+' : ''}
                            {formatCurrency(totalStatsProfit)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-xs text-gray-400 hidden md:table-cell" data-id="element-5226" data-mp="1ozw6o252aphx">
                          100%
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  {/* Pagination for detail table */}
                  {statsTableTotalPages > 1 && <div className="p-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-4" data-id="element-5227" data-mp="1fdbkxikc2sy2">
                      <span className="text-xs text-gray-500" data-id="element-5228" data-mp="1abvz3oac0iem">
                        Mostrando {statsTableStartIndex + 1}–
                        {statsTableEndIndex} de {statsTableTotalItems} productos
                      </span>
                      <Pagination currentPage={statsTablePage} totalPages={statsTableTotalPages} onPageChange={statsTableGoToPage} data-id="element-5229" data-mp="1y2i3n5l4nkjv" />
                
                    </div>}
                </div>
              </>}
          </Card>
        </div>}

      {/* ── ARCHIVED TAB ──────────────────────────────────────────────────── */}
      {activeTab === 'archived' && <Card padding="none" data-id="element-5230" data-mp="np0h8onb2sde">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3" data-id="element-5231" data-mp="nrwesbv8rf1b">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg" data-id="element-5232" data-mp="sqg3ul54t6xw">
              <ArchiveIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" data-id="element-5233" data-mp="xohimtviyk3k" />
            </div>
            <div data-id="element-5234" data-mp="12m186pxoajat">
              <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-5235" data-mp="12kgd8z7g4ycf">
                Productos Archivados
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5236" data-mp="9xc6opspgbyj">
                {archivedProducts.length} producto
                {archivedProducts.length !== 1 ? 's' : ''} archivado
                {archivedProducts.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {archivedProducts.length === 0 ? <div className="py-16 text-center" data-id="element-5237" data-mp="3idovdtmvwxgf">
              <ArchiveIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-5238" data-mp="3nci7bhmiwgeh" />
              <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-5239" data-mp="24iffgydft9od">
                No hay productos archivados
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-5240" data-mp="20gtu0omu7n2k">
                Los productos que archive aparecerán aquí
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-5241" data-mp="1qinwskbz7541">
              <table className="w-full" data-id="element-5242" data-mp="1qhway186klqy">
                <thead data-id="element-5243" data-mp="1ljkz5kybs163">
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/10" data-id="element-5244" data-mp="1lk3210fhtdmz">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-5245" data-mp="1gqrv6zaokwv2">
                      Producto
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell" data-id="element-5246" data-mp="17s6ezasoltxg">
                      SKU
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell" data-id="element-5247" data-mp="12uefnvppj5j8">
                      Categoría
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-5248" data-mp="7r9rkofyy6uf">
                      Precio
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-5249" data-mp="gq3grtqyzuxy">
                      Restaurar
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5250" data-mp="3k7lp02ann3yw">
                  {archivedProducts.map(product => <tr key={product.id} className="hover:bg-amber-50/50 dark:hover:bg-amber-900/10 opacity-75" data-id="element-5251" data-mp="3g6tdt3i82ozz">
                
                      <td className="py-3 px-4" data-id="element-5252" data-mp="3p4lwmhn5kes4">
                        <p className="font-medium text-gray-700 dark:text-gray-300" data-id="element-5253" data-mp="3p3u116tk2h1u">
                          {product.name}
                        </p>
                        <p className="text-xs text-gray-400 truncate max-w-[200px]" data-id="element-5254" data-mp="2wfy3jzw5wl40">
                          {product.description}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400 hidden sm:table-cell" data-id="element-5255" data-mp="1gpx7lfkedh0w">
                        {product.sku}
                      </td>
                      <td className="py-3 px-4 hidden sm:table-cell" data-id="element-5256" data-mp="17x3rjfx4mdlz">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400" data-id="element-5257" data-mp="17v96m1luulhw">
                          <TagIcon className="w-3 h-3" data-id="element-5258" data-mp="1znzu9nph31vj" />
                          {product.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400" data-id="element-5259" data-mp="1qnbp7ii24bus">
                        {formatCurrency(product.price)}
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-5260" data-mp="abo5og9bmoih">
                        <button onClick={() => handleUnarchiveProduct(product.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 rounded-lg transition-colors" title="Restaurar al catálogo" data-id="element-5261" data-mp="f9hdfdq9vqkv">
                    
                          <ArchiveRestoreIcon className="w-3.5 h-3.5" data-id="element-5262" data-mp="3dk1eci6l1vph" />
                          Restaurar
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </Card>}

      {/* Product Modal */}
      <Modal isOpen={isProductModalOpen} onClose={() => setIsProductModalOpen(false)} title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'} size="md" icon={<PackageIcon className="w-6 h-6 text-white" data-id="element-5264" data-mp="xr2ip1czdlmf" />} accentColor="amber" data-id="element-5263" data-mp="2zmmmz7wrrduv">
        
        <form onSubmit={handleSaveProduct} className="space-y-6" data-id="element-5265" data-mp="ss92302edei1">
          <FormSection title="Información Básica" data-id="element-5266" data-mp="10d1swn8apuag">
            <div className="space-y-4" data-id="element-5267" data-mp="15cxy2psqbrrl">
              <Input label="Nombre del Producto" value={productForm.name} onChange={e => setProductForm({
              ...productForm,
              name: e.target.value
            })} error={productErrors.name} data-id="element-5268" data-mp="15bwdnuptpsx7" />
              

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5269" data-mp="hhbpqwommruz">
                <Input label="SKU (Código)" value={productForm.sku} onChange={e => setProductForm({
                ...productForm,
                sku: e.target.value
              })} error={productErrors.sku} data-id="element-5270" data-mp="melmyyi9c7td" />
                

                <Select label="Categoría" options={CATEGORIES} value={productForm.category} onChange={v => setProductForm({
                ...productForm,
                category: v
              })} data-id="element-5271" data-mp="26tdvr1m2c0cd" />
                
              </div>
              <Textarea label="Descripción" value={productForm.description} onChange={e => setProductForm({
              ...productForm,
              description: e.target.value
            })} rows={2} data-id="element-5272" data-mp="2f8ur1hh0dchh" />
              
            </div>
          </FormSection>

          <FormSection title="Inventario y Precios" data-id="element-5273" data-mp="2y0439yi5yfcr">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-5274" data-mp="2t2m24uae14fb">
              <Input type="number" label="Costo" value={productForm.cost} onChange={e => setProductForm({
              ...productForm,
              cost: e.target.value
            })} error={productErrors.cost} data-id="element-5275" data-mp="2t4f7m699lilq" />
              

              <Input type="number" label="Precio Venta" value={productForm.price} onChange={e => setProductForm({
              ...productForm,
              price: e.target.value
            })} error={productErrors.price} data-id="element-5276" data-mp="3qmubu32u369p" />
              

              <Input type="number" label="Stock" value={productForm.stock} onChange={e => setProductForm({
              ...productForm,
              stock: e.target.value
            })} error={productErrors.stock} data-id="element-5277" data-mp="37orgl6jp0d3g" />
              

              <Input type="number" label="Stock Mínimo (alerta)" placeholder="5" value={productForm.minStock} onChange={e => setProductForm({
              ...productForm,
              minStock: e.target.value
            })} hint="Se enviará una alerta cuando el stock llegue a este número o menos." data-id="element-5278" data-mp="2k08gheasr6mg" />
              
            </div>
            <div className="mt-4" data-id="element-5279" data-mp="gs7se5rtref4">
              <Select label="Estado" options={[{
              value: 'active',
              label: 'Activo'
            }, {
              value: 'inactive',
              label: 'Inactivo'
            }]} value={productForm.status} onChange={v => setProductForm({
              ...productForm,
              status: v as any
            })} data-id="element-5280" data-mp="gp3koyf5i0jp" />
              
            </div>
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800 modal-actions" data-id="element-5281" data-mp="3k9q1egjc2rh4">
            <Button variant="outline" type="button" onClick={() => setIsProductModalOpen(false)} data-id="element-5282" data-mp="3f2t0kpadlr6b">
              
              Cancelar
            </Button>
            <Button type="submit" data-id="element-5283" data-mp="3osmbrp6lw4a1">
              {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Sale Modal */}
      <Modal isOpen={isSaleModalOpen} onClose={() => {
      setIsSaleModalOpen(false);
      setEditingSale(null);
    }} title={editingSale ? 'Editar Venta' : 'Registrar Venta'} size="lg" icon={<ShoppingCartIcon className="w-6 h-6 text-white" data-id="element-5285" data-mp="bmciztk6xd87" />} accentColor="amber" data-id="element-5284" data-mp="3swk9pwk7391i">
        
        {editingSale && <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-300" data-id="element-5286" data-mp="28fxhykoohyh2">
            <AlertTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-5287" data-mp="j4an1ijlrw41" />
            <span data-id="element-5288" data-mp="e7ahr9ukuxdz">
              Estás editando una venta existente. El stock se ajustará
              automáticamente. Este cambio quedará registrado en la bitácora.
            </span>
          </div>}
        <form onSubmit={handleSaveSale} className="space-y-6" data-id="element-5289" data-mp="nxd8f3e87z95">
          <FormSection title="Detalles de la Venta" data-id="element-5290" data-mp="6q0ii4b99ml">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5291" data-mp="79gvuebbpyi">
              <Select label="Cliente / Paciente" options={patients.map(p => ({
              value: p.id,
              label: p.name
            }))} value={saleForm.patientId} onChange={v => setSaleForm({
              ...saleForm,
              patientId: v
            })} error={saleErrors.patientId} searchable data-id="element-5292" data-mp="3rdu2uzpig7oo" />
              

              <Select label="Vendedor" options={sellers.map(s => ({
              value: s.id,
              label: s.name
            }))} value={saleForm.sellerId} onChange={v => setSaleForm({
              ...saleForm,
              sellerId: v
            })} error={saleErrors.sellerId} searchable placeholder="Seleccionar vendedor..." data-id="element-5293" data-mp="1o3z3l4l2k1qg" />
              

              <Input type="date" label="Fecha" value={saleForm.date} onChange={e => setSaleForm({
              ...saleForm,
              date: e.target.value
            })} error={saleErrors.date} data-id="element-5294" data-mp="2bsrv8ounrnf9" />
              
            </div>
          </FormSection>

          <FormSection title="Productos" data-id="element-5295" data-mp="2ulm91fgjp9hw">
            {saleErrors.items && <p className="text-sm text-red-500 mb-2" data-id="element-5296" data-mp="pzr8wyedh0ts">{saleErrors.items}</p>}
            <div className="space-y-3" data-id="element-5297" data-mp="uz472fap1ubo">
              {saleItems.map((item, idx) => <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-5298" data-mp="v16tyqgjv7c6" data-mpi={idx}>
                
                  <div className="flex-1" data-id="element-5299" data-mp="79t3kq1ylltu">
                    <Select options={products.filter(p => p.status === 'active').map(p => ({
                  value: p.id,
                  label: `${p.name} (${p.stock} disp.) - ${formatCurrency(p.price)}`
                }))} value={item.productId} onChange={v => updateSaleItem(idx, 'productId', v)} searchable placeholder="Seleccionar producto..." data-id="element-5300" data-mp="78qfsqzxnzi5" />
                  

                    {saleErrors[`item_${idx}`] && <p className="text-xs text-red-500 mt-1" data-id="element-5301" data-mp="3h5yzs3zf16lg">
                        {saleErrors[`item_${idx}`]}
                      </p>}
                  </div>
                  <div className="w-20" data-id="element-5302" data-mp="3qwbj5svhutq0">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block" data-id="element-5303" data-mp="3qy4o93qgl5vw">
                      Cant.
                    </label>
                    <Input type="number" min="1" value={item.quantity.toString()} onChange={e => updateSaleItem(idx, 'quantity', parseInt(e.target.value) || 1)} data-id="element-5304" data-mp="3rlttera817a" />
                  
                  </div>
                  <div className="w-20" data-id="element-5305" data-mp="2fgwkmknmztss">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block" data-id="element-5306" data-mp="2fhogzxp25vk4">
                      Desc. %
                    </label>
                    <Input type="number" min="0" max="100" value={item.discount.toString()} onChange={e => updateSaleItem(idx, 'discount', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))} data-id="element-5307" data-mp="306z3z1jfg90l" />
                  
                  </div>
                  {item.productId && item.discount > 0 && <div className="flex flex-col justify-end pb-1" data-id="element-5308" data-mp="3rx50askdhruq">
                      <span className="text-xs text-gray-400 line-through" data-id="element-5309" data-mp="3rzzkcizh5anm">
                        {formatCurrency((products.find(p => p.id === item.productId)?.price || 0) * item.quantity)}
                      </span>
                      <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-id="element-5310" data-mp="anf67nay0c9v">
                        {formatCurrency((products.find(p => p.id === item.productId)?.price || 0) * item.quantity * (1 - item.discount / 100))}
                      </span>
                    </div>}
                  <button type="button" onClick={() => removeSaleItem(idx)} className="p-2 mt-1 text-red-500 hover:bg-red-50 rounded-lg" disabled={saleItems.length === 1} data-id="element-5311" data-mp="2dxs9x2c7a2r3">
                  
                    <TrashIcon className="w-5 h-5" data-id="element-5312" data-mp="2465rakxj91eb" />
                  </button>
                </div>)}
              <Button type="button" variant="outline" size="sm" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-5314" data-mp="2rxb6cxvnfpnk" />} onClick={addSaleItem} data-id="element-5313" data-mp="2wojhpzgjhl3d">
                
                Agregar otro producto
              </Button>
            </div>
            <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl flex items-center justify-between border border-orange-100 dark:border-orange-800" data-id="element-5315" data-mp="2hewludufb4hw">
              <span className="text-orange-800 dark:text-orange-300 font-medium" data-id="element-5316" data-mp="22jglxs75wedl">
                Total de la Venta:
              </span>
              <span className="text-2xl font-bold text-orange-600 dark:text-orange-400" data-id="element-5317" data-mp="2v6kxkg0rfqyc">
                {formatCurrency(calculateSaleTotal())}
              </span>
            </div>
          </FormSection>

          <FormSection title="Notas Adicionales" data-id="element-5318" data-mp="2lh1bz2lnxizs">
            <Textarea value={saleForm.notes} onChange={e => setSaleForm({
            ...saleForm,
            notes: e.target.value
          })} rows={2} placeholder="Observaciones sobre la venta..." data-id="element-5319" data-mp="2lg83nx722lfj" />
            
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800 modal-actions" data-id="element-5320" data-mp="17gn52sdpgf71">
            <Button variant="outline" type="button" onClick={() => {
            setIsSaleModalOpen(false);
            setEditingSale(null);
          }} data-id="element-5321" data-mp="12ill858ggbui">
              
              Cancelar
            </Button>
            <Button type="submit" leftIcon={<DollarSignIcon className="w-4 h-4" data-id="element-5323" data-mp="oikj6mggeydg" />} data-id="element-5322" data-mp="oi11n6upubzy">
              
              {editingSale ? 'Guardar Cambios' : 'Registrar Venta'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Archive Confirm Modal ─────────────────────────────────────── */}
      {archiveConfirmProduct && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-5324" data-mp="tfu71xo731z6">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !archiveLoading && setArchiveConfirmProduct(null)} data-id="element-5325" data-mp="28zyxkjnlawc1" />
        

          {/* Card */}
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-5326" data-mp="11nhfiavqyhr3">
            {/* Top accent bar */}
            <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" data-id="element-5327" data-mp="1bkcf6x3rao6y" />
            <div className="p-6" data-id="element-5328" data-mp="1fj1xn43nivxg">
              {/* Icon + title */}
              <div className="flex items-start gap-4 mb-4" data-id="element-5329" data-mp="iw6pdvxui909">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center" data-id="element-5330" data-mp="ntqe5ps6646g">
                  <ArchiveIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" data-id="element-5331" data-mp="rus75ovhtjzz" />
                </div>
                <div data-id="element-5332" data-mp="wnv0bvwcyrfn">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5333" data-mp="woehvbi3jdt5">
                    ¿Archivar producto?
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-5334" data-mp="457kauw7y2lf">
                    Esta acción no eliminará el producto permanentemente.
                  </p>
                </div>
              </div>

              {/* Product info */}
              <div className="mb-5 p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" data-id="element-5335" data-mp="3nqyf7hxp2x63">
                <div className="flex items-center gap-3" data-id="element-5336" data-mp="3escy7r7vfq7f">
                  <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center" data-id="element-5337" data-mp="3ertjskhfjjy5">
                    <PackageIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" data-id="element-5338" data-mp="3529x13qlj5y0" />
                  </div>
                  <div data-id="element-5339" data-mp="302x90fnznstu">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm" data-id="element-5340" data-mp="3048dyit676mj">
                      {archiveConfirmProduct.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5341" data-mp="2r2ilrargs3np">
                      SKU: {archiveConfirmProduct.sku} ·{' '}
                      {archiveConfirmProduct.category}
                    </p>
                  </div>
                </div>
              </div>

              {/* Info note */}
              <div className="flex items-start gap-2 p-3 mb-6 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300" data-id="element-5342" data-mp="iuc7nil5pgu2">
                <ArchiveRestoreIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-5343" data-mp="9svwh8mipaos" />
                <span data-id="element-5344" data-mp="9uywmlhdwk78">
                  Podrás restaurarlo en cualquier momento desde la pestaña{' '}
                  <strong data-id="element-5345" data-mp="3w0c99nasjuom">Productos Archivados</strong>.
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-3" data-id="element-5346" data-mp="3i9qoft1s1ao3">
                <button onClick={() => setArchiveConfirmProduct(null)} disabled={archiveLoading} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50" data-id="element-5347" data-mp="3iasasto8tjl4">
                
                  Cancelar
                </button>
                <button onClick={confirmArchiveProduct} disabled={archiveLoading} className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-semibold shadow-md shadow-orange-200 dark:shadow-orange-900/30 transition-all disabled:opacity-70 flex items-center justify-center gap-2" data-id="element-5348" data-mp="2oozohpvsis4o">
                
                  {archiveLoading ? <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" data-id="element-5349" data-mp="3mcqfz9899trs">
                    
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-5350" data-mp="3hfqbv3uyv13b" />
                    

                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" data-id="element-5351" data-mp="wjsz4jq8hsga" />
                    
                      </svg>
                      Archivando...
                    </> : <>
                      <ArchiveIcon className="w-4 h-4" data-id="element-5352" data-mp="ikktb7zkfm34" />
                      Sí, archivar
                    </>}
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Productos" isExporting={isExporting} data-id="element-5353" data-mp="3w6tl5xg4dqs" />
      

      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Productos a PDF" isExporting={isPdfExporting} data-id="element-5354" data-mp="3r04j1x112mdf" />
      

      {/* Sales Export Modals */}
      <ExcelExportModal isOpen={isSalesExcelModalOpen} onClose={() => setIsSalesExcelModalOpen(false)} onExport={handleSalesExportToExcel} title="Exportar Ventas a Excel" isExporting={isSalesExcelExporting} data-id="element-5355" data-mp="26ivegivrc0y7" />
      

      <PdfExportModal isOpen={isSalesPdfModalOpen} onClose={() => setIsSalesPdfModalOpen(false)} onExport={handleSalesExportToPdf} title="Exportar Ventas a PDF" isExporting={isSalesPdfExporting} data-id="element-5356" data-mp="2fj0kaolayra9" />
      

      {/* Stats Export Modals */}
      <ExcelExportModal isOpen={isStatsExcelModalOpen} onClose={() => setIsStatsExcelModalOpen(false)} onExport={handleStatsExportToExcel} title="Exportar Estadísticas a Excel" isExporting={isStatsExcelExporting} data-id="element-5357" data-mp="1rtfq19jp87lo" />
      

      <PdfExportModal isOpen={isStatsPdfModalOpen} onClose={() => setIsStatsPdfModalOpen(false)} onExport={handleStatsExportToPdf} title="Exportar Estadísticas a PDF" isExporting={isStatsPdfExporting} data-id="element-5358" data-mp="144d77ydbw8pl" />
      
    </div>;
}